import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { BILLING_CATEGORIES, VALUE_TYPES } from '../billingCategories';
import { useApp } from '../appContext';
import { activeTabKey } from './registerTabs';
import { projectPath, useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { PROJECT_SETUP_STATUSES, PROJECT_STATUSES, PROJECT_TYPES, useGridSort, sortRows, lower } from './Configuration';
import SOWIntake from './SOWIntake';
import ProjectEventModal from './ProjectEventForm';

// Replaces the SOW Intake / Project Setup nav entries (2026-09-07): the "Project Launch" nav item
// opens this one page, three views switched via `?view=` -- `details` (default, the go-to hub for
// a project underway, redesigned 2026-09-10), `setup` (the one-time launch checklist, still the
// old ProjectSetup wizard merged into always-visible sections) and `activity`. These stopped being
// tabs on 2026-09-10 (Jeremy) -- Setup/Activity are reached via small links instead, since a PM
// spends most of their time on Details.
//
// SOW Intake stopped being its own tab on 2026-09-08 (item 7): a PM opens it from the specific
// placeholder it belongs to, in PlaceholderActionsPanel, not from a picker that could target any
// placeholder -- the system already knows which one, and it comes with conflict-aware merging
// instead of a blind overwrite. `SOWIntake` takes `lockedPlaceholderId` for that case, skipping its
// own picker/suggestion UI entirely. A SOW for work nobody forecast in advance has no placeholder to
// open that from, so `ProjectSelectorBar`'s "Load SOW →" button still opens the unlocked SOWIntake,
// picker and all -- the net-new case the standalone tab used to cover, now a button instead of a
// permanent tab.

// Folder creation and customer subsite creation still run on Power Automate under the hood (ADR
// 0011); `REACT_APP_PROVISIONING_SOURCE` toggles the proxy path the same way core/intake's own
// migrations did. Carried over from ProjectSetup.js verbatim.
const PROVISIONING_SOURCE = process.env.REACT_APP_PROVISIONING_SOURCE || 'legacy';
const CREATE_FOLDERS_URL = process.env.REACT_APP_CREATE_FOLDERS_URL;
const CREATE_CUSTOMER_SUBSITE_URL = process.env.REACT_APP_CREATE_CUSTOMER_SUBSITE_URL;

// Trimmed: "ID - Bonneville - X" splits on '-' to "ID - Bonneville " and SharePoint rejects folder
// names with a trailing space (the flow's CreateNewFolder 502s on it).
function folderParts(name) {
  const parts = name.split('-');
  return { customer: parts.slice(0, 2).join('-').trim(), project: parts.slice(2).join('-').trim() };
}

const RAG_STATUS_VALUES = ['Red', 'Amber', 'Green'];

// Excludes 'Archived' (nothing new is created archived) and 'Placeholder' (that's a different row
// shape -- isPlaceholder:true, only ever set via Project Configuration's own placeholder card; this
// form always creates a real project, so offering that status here would produce a project row that
// LOOKS like a placeholder but can't reach PlaceholderActionsPanel's Load SOW/Promote actions, which
// gate on the boolean, not the status string).
const NEW_PROJECT_STATUS_VALUES = PROJECT_STATUSES.filter((s) => s !== 'Archived' && s !== 'Placeholder');

// Mirrors CHECKLIST_CATEGORIES in packages/shared -- apps/web can't import it (ADR 0019: react-scripts'
// ModuleScopePlugin refuses a symlinked workspace package, which is what apps/web's own build breaks
// on even though a monorepo-root `npm install` locally makes the import resolve anyway, silently,
// right up until the isolated Azure Static Web Apps build hits it for real), and this is too small to
// be worth a round trip to the API.
const CHECKLIST_CATEGORIES = [
  'Project Planning',
  'Resource Assignments',
  'Project Metrics',
  'Kickoff(s)',
  'Project Administration',
];

const NEEDS_SETUP_STATUSES = ['Placeholder', 'Startup'];

// Labels the project's own lifecycle status, not the checklist's completion -- the two "not
// started"s (a project barely begun vs. a checklist nobody's touched yet) read as the same word
// otherwise (Jeremy, 2026-09-07).
const STATUS_BADGE_CLASS = {
  Placeholder: 'badge-yellow',
  Startup: 'badge-yellow',
  Active: 'badge-blue',
  'Final Acceptance': 'badge-blue',
  Converted: 'badge-blue',
  Completed: 'badge-green',
};

function StatusBadge({ status }) {
  return <span className={`badge ${STATUS_BADGE_CLASS[status] || 'badge-blue'}`}>{status}</span>;
}

// T&M is hidden by default -- it has no fixed-fee plan for a checklist to track against (same
// reasoning ProjectRegisters' picker restricts to POC); one checkbox away when a PM wants it
// (Jeremy, 2026-09-07). Placeholder defaults to shown (Jeremy, 2026-09-09) -- pre-SOW forecasting
// rows are exactly what a PM comes to Project Setup to promote or load a SOW against.
function groupProjects(projects, { showTAndM, showPlaceholder }) {
  const needsSetup = [];
  const inDelivery = [];
  for (const p of projects) {
    if (p.projectStatus === 'Archived' || p.mergedIntoProjectId || p.isAdministrative) continue;
    if (!showTAndM && p.projectType === 'T&M') continue;
    if (!showPlaceholder && p.projectStatus === 'Placeholder') continue;
    (NEEDS_SETUP_STATUSES.includes(p.projectStatus) ? needsSetup : inDelivery).push(p);
  }
  return { needsSetup, inDelivery };
}

export default function ProjectLaunch() {
  const { currentUser, permissions } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const canViewActivity = isAdmin || !!permissions?.isManagerOfAnyone || !!permissions?.isProjectManagerOfAny;

  const [searchParams] = useSearchParams();
  // 'details' is first/default (Jeremy, 2026-09-09): once a project is set up, this is where a PM
  // spends day to day, with 'setup' reached via a link for the one-time launch checklist. All three
  // views share the same selector below -- switching views with a project selected re-renders the
  // same project under the other view, rather than each view owning its own selection. `tabs` here
  // is just `activeTabKey`'s known-keys list now (no TabBar rendered, 2026-09-10).
  const tabs = [
    { key: 'details', label: 'Project Details' },
    { key: 'setup', label: 'Project Setup' },
    ...(canViewActivity ? [{ key: 'activity', label: 'PM Activity' }] : []),
  ];
  const view = activeTabKey(searchParams.get('view'), tabs);

  // Restores whichever project was last selected on any of these pages (ADR 0042), or a
  // `?project=<id>` deep link (ADR 0038).
  const [selectedProjectId, setSelectedProjectId] = useSelectedProject();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // A SOW for work nobody forecast in advance has no placeholder to open "Load SOW" from (that
  // button only exists inside a specific placeholder's own detail view) -- this is the net-new
  // equivalent, reachable from the list instead of a persistent tab. Same SOWIntake component, just
  // without lockedPlaceholderId, so its own placeholder picker (including "create a new project")
  // still works exactly as it did as a standalone tab.
  const [showNetNewIntake, setShowNetNewIntake] = useState(false);

  async function reloadProjects() {
    try {
      setProjects(await apiFetch('/projects'));
    } catch (err) {
      setError('Could not load projects — ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reloadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePruneMissingProject(selectedProjectId, setSelectedProjectId, projects, !loading);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16, flexWrap:'wrap' }}>
        <div>
          <div className="page-eyebrow">Professional Services</div>
          <h1 className="page-title">{view === 'setup' ? 'Project Setup' : view === 'activity' ? 'PM Activity' : 'Project Details'}</h1>
          <p className="page-subtitle">
            {view === 'setup'
              ? "A PM's checklist and launch point for standing up a project — customizable per project."
              : view === 'activity'
                ? "Checklist activity across every PM's projects."
                : 'The hub for a project already underway — info, health, upcoming activity, milestones, budget and travel.'}
          </p>
        </div>
        {/* Setup and PM Activity used to be tabs on this page; they're links now (Jeremy, 2026-09-10) --
            small and out of the way, stacked vertically (Jeremy, 2026-09-10), Setup above Activity. */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, fontSize:12, paddingTop:8, whiteSpace:'nowrap' }}>
          {view !== 'details' && <Link to="/project-launch?view=details">Project Details →</Link>}
          {view !== 'setup' && <Link to="/project-launch?view=setup">Project Setup →</Link>}
          {canViewActivity && view !== 'activity' && <Link to="/project-launch?view=activity">PM Activity →</Link>}
        </div>
      </div>

      {/* PM Activity has no per-project content, so the selector (and its "load a SOW" detour) only
          makes sense for the other two views. Create/load actions only show on Setup (Jeremy,
          2026-09-10) -- Details is the ongoing hub, not where a PM starts something new. */}
      {view !== 'activity' && !showNetNewIntake && (
        <ProjectSelectorBar
          projects={projects}
          loading={loading}
          error={error}
          selectedProjectId={selectedProjectId}
          onSelect={setSelectedProjectId}
          showCreateActions={view === 'setup'}
          onLoadSow={() => setShowNetNewIntake(true)}
          onProjectCreated={async (id) => { await reloadProjects(); setSelectedProjectId(id); }}
        />
      )}

      {view === 'activity' ? (
        <PmActivityPanel />
      ) : showNetNewIntake ? (
        <div>
          {/* Not `onDone` -- that's the locked flow's "one SOW, then leave" button. This is the old
              standalone tab's behavior, unchanged: stay here and start another intake, or leave
              whenever via this back button, which is the one place that needs to pick up anything
              just committed. */}
          <button
            className="btn btn-outline"
            style={{ marginBottom:16 }}
            onClick={() => { setShowNetNewIntake(false); reloadProjects(); }}
          >
            ← Back to projects
          </button>
          <SOWIntake />
        </div>
      ) : selectedProject ? (
        view === 'details' ? (
          <ProjectDetailsDashboard project={selectedProject} onProjectChanged={reloadProjects} />
        ) : (
          <ProjectSetupDetail project={selectedProject} isAdmin={isAdmin} onProjectChanged={reloadProjects} />
        )
      ) : !loading && (
        <div style={{ textAlign:'center', padding:32, color:'var(--text-mid)', fontSize:13 }}>
          Select a project above, or create a new one.
        </div>
      )}
    </div>
  );
}

// ── PROJECT SELECTOR (dropdown, matching Project Schedule's own picker) ─────
// Replaces the old card-list browser (Jeremy, 2026-09-10) -- same groupProjects() filter, just an
// <optgroup>-organized <select> instead of two sections of clickable cards.
function ProjectSelectorBar({ projects, loading, error, selectedProjectId, onSelect, showCreateActions, onLoadSow, onProjectCreated }) {
  const [showTAndM, setShowTAndM] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const { needsSetup, inDelivery } = groupProjects(projects, { showTAndM, showPlaceholder });

  if (loading) {
    return <div style={{ textAlign:'center', padding:32, color:'var(--text-mid)', fontSize:13 }}>Loading projects…</div>;
  }
  if (error) {
    return (
      <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'rgba(239,65,54,0.08)',
        border:'1px solid rgba(239,65,54,0.3)', fontSize:13, color:'var(--red)' }}>
        ⚠ {error}
      </div>
    );
  }

  function optionLabel(p) {
    return p.account?.name ? `${p.name} · ${p.account.name}` : p.name;
  }

  return (
    <>
      <div className="card" style={{ marginBottom:20 }}>
        <div style={{ display:'flex', gap:16, alignItems:'flex-end', flexWrap:'wrap' }}>
          <div className="form-group" style={{ marginBottom:0, flex:1, minWidth:280, maxWidth:520 }}>
            <label className="form-label" htmlFor="project-launch-project">Project</label>
            <select
              id="project-launch-project" className="form-input"
              value={selectedProjectId} onChange={(e) => onSelect(e.target.value)}
              disabled={!projects.length}
            >
              <option value="">{projects.length ? '— Select a project —' : 'No projects yet'}</option>
              {needsSetup.length > 0 && (
                <optgroup label="Needs Setup">
                  {needsSetup.map((p) => <option key={p.id} value={p.id}>{optionLabel(p)}</option>)}
                </optgroup>
              )}
              {inDelivery.length > 0 && (
                <optgroup label="In Delivery">
                  {inDelivery.map((p) => <option key={p.id} value={p.id}>{optionLabel(p)}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text-mid)', cursor:'pointer', paddingBottom:10, whiteSpace:'nowrap' }}>
            <input type="checkbox" checked={showTAndM} onChange={(e) => setShowTAndM(e.target.checked)} />
            Show T&amp;M
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text-mid)', cursor:'pointer', paddingBottom:10, whiteSpace:'nowrap' }}>
            <input type="checkbox" checked={showPlaceholder} onChange={(e) => setShowPlaceholder(e.target.checked)} />
            Show Placeholder
          </label>
          {showCreateActions && (
            <div style={{ display:'flex', gap:8, marginLeft:'auto', paddingBottom:2 }}>
              <button className="btn btn-outline" onClick={() => setShowNewProject(true)}>+ New project</button>
              {/* For a SOW that has no placeholder to open "Load SOW" from -- work nobody forecast in
                  advance. A placeholder's own detail view is still the normal path (it comes with
                  conflict-aware merging); this is the net-new fallback the standalone tab used to cover. */}
              <button className="btn btn-outline" onClick={onLoadSow}>Load SOW →</button>
            </div>
          )}
        </div>
      </div>
      {showCreateActions && showNewProject && (
        <NewProjectForm
          onCreated={async (id) => { setShowNewProject(false); await onProjectCreated(id); }}
          onCancel={() => setShowNewProject(false)}
        />
      )}
    </>
  );
}

// A PM launching work nobody's sent a SOW for yet still needs a project row to hang the checklist
// off of, and "the right account doesn't exist" is the same blocker SOW Intake's own picker runs
// into -- hence the inline account creation rather than a trip to Project Configuration.
function NewProjectForm({ onCreated, onCancel }) {
  const [accounts, setAccounts] = useState([]);
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [productName, setProductName] = useState('');
  const [projectType, setProjectType] = useState('POC');
  // Defaults to Startup, not the DTO's own Active -- matches ProjectsService.promotePlaceholder's
  // freshly-promoted row (nothing's provisioned yet), but stays editable here for a project that's
  // already underway or one that's still just a forecast.
  const [projectStatus, setProjectStatus] = useState('Startup');
  const [startDate, setStartDate] = useState('');
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountState, setNewAccountState] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  useEffect(() => {
    // Merge rather than replace: if this GET is still in flight when the user adds an account via
    // handleCreateAccount below, a plain setAccounts(rows) here would overwrite it right back out.
    apiFetch('/accounts')
      .then((rows) => setAccounts((prev) => {
        const byId = new Map(rows.map((a) => [a.id, a]));
        for (const a of prev) if (!byId.has(a.id)) byId.set(a.id, a);
        return [...byId.values()];
      }))
      .catch(() => {});
  }, []);

  async function handleCreateAccount() {
    if (!newAccountName.trim() || creatingAccount) return;
    setCreatingAccount(true);
    setError('');
    try {
      const created = await apiFetch('/accounts', {
        method: 'POST',
        body: JSON.stringify({ name: newAccountName.trim(), state: newAccountState.trim() || undefined }),
      });
      setAccounts((prev) => [...prev, created]);
      setAccountId(created.id);
      setNewAccountName('');
      setNewAccountState('');
      setShowNewAccount(false);
    } catch (err) {
      setError('Could not add account — ' + err.message);
    } finally {
      setCreatingAccount(false);
    }
  }

  async function handleCreate() {
    if (!name.trim() || !accountId || saving) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          accountId,
          productName: productName.trim() || undefined,
          projectType,
          startDate: startDate || undefined,
          projectStatus: isPlaceholder ? 'Placeholder' : projectStatus,
          isPlaceholder,
        }),
      });
      await onCreated(created.id);
    } catch (err) {
      setError('Could not create project — ' + err.message);
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="card-title">New project</div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10, maxWidth:420 }}>
        <input
          type="text" className="form-input" placeholder="Project name" maxLength={300}
          value={name} onChange={(e) => setName(e.target.value)}
        />

        {!showNewAccount ? (
          <div style={{ display:'flex', gap:8 }}>
            <select className="form-select" style={{ flex:1 }} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">— Account —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button className="btn btn-outline" onClick={() => setShowNewAccount(true)}>+ New account</button>
          </div>
        ) : (
          <div style={{ display:'flex', gap:8, alignItems:'flex-end', padding:10, border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
            <div style={{ flex:1 }}>
              <label className="form-label">New account</label>
              <input
                type="text" className="form-input" placeholder="e.g. Ada County" maxLength={200}
                value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)}
              />
            </div>
            <div style={{ width:80 }}>
              <label className="form-label">State</label>
              <input
                type="text" className="form-input" placeholder="ID" maxLength={50}
                value={newAccountState} onChange={(e) => setNewAccountState(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" disabled={creatingAccount || !newAccountName.trim()} onClick={handleCreateAccount}>
              {creatingAccount ? 'Adding…' : 'Add'}
            </button>
            <button
              className="btn btn-outline"
              onClick={() => { setShowNewAccount(false); setNewAccountName(''); setNewAccountState(''); }}
            >
              Cancel
            </button>
          </div>
        )}

        <input
          type="text" className="form-input" placeholder="Product name (optional)" maxLength={200}
          value={productName} onChange={(e) => setProductName(e.target.value)}
        />
        <select className="form-select" value={projectType} onChange={(e) => setProjectType(e.target.value)}>
          {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="form-select" value={projectStatus} disabled={isPlaceholder} onChange={(e) => setProjectStatus(e.target.value)}>
          {NEW_PROJECT_STATUS_VALUES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--text-mid)' }}>
          <input type="checkbox" checked={isPlaceholder} onChange={(e) => setIsPlaceholder(e.target.checked)} />
          This is a placeholder (forecast, no SOW yet)
        </label>
      </div>
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:8 }}>⚠ {error}</div>}
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary" disabled={saving || !name.trim() || !accountId} onClick={handleCreate}>
          {saving ? 'Creating…' : 'Create project'}
        </button>
        {/* Disabled while saving, not just hidden -- onCreated is a closure over this request; letting
            the form unmount mid-request would let its result land on whatever form is open when the
            POST finally resolves. */}
        <button className="btn btn-outline" disabled={saving} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const RAG_BADGE_CLASS = { Red: 'badge-red', Amber: 'badge-yellow', Green: 'badge-green' };

// ── COLLAPSIBLE CARD ─────────────────────────────────────────
// Every frame on the Project Details hub collapses (Jeremy, 2026-09-10) -- same ▾/▸-on-the-title
// convention Project Setup's checklist categories already use, just as a standalone wrapper so it
// doesn't need re-deriving per frame. Uncontrolled (each card owns its own open/closed state) --
// nothing here needs it to persist across a project switch or a page reload.
function CollapsibleCard({ id, title, actions, defaultCollapsed = false, children }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div className="card" id={id} style={{ marginBottom:16 }}>
      <div
        className="card-title"
        style={{ cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <span>{collapsed ? '▸' : '▾'} {title}</span>
        {actions && (
          <span style={{ fontSize:12, fontWeight:400 }} onClick={(e) => e.stopPropagation()}>
            {actions}
          </span>
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}

// ── PROJECT DETAILS DASHBOARD (redesigned, 2026-09-10) ──────
// The go-to hub for a project already underway -- compact, multiple frames, minimal scrolling.
// Feeds the future Status Report and Portfolio Dashboard (Jeremy): Health becomes
// StatusReportSnapshot rows, Budget reads BudgetLine. Full milestone editing moved to Project
// Setup (MilestonesSection there) -- this only shows a full-detail read-only table.
// Two columns (Jeremy, 2026-09-10): Info+Budget on the left, Health+KPIs on the right -- Info and
// Health are the two a PM checks most, so each leads its own column.
function ProjectDetailsDashboard({ project, onProjectChanged }) {
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, alignItems:'start' }}>
        <div>
          <ProjectInfoPanel project={project} onSaved={onProjectChanged} />
          <BudgetSummaryPanel project={project} />
        </div>
        <div>
          <HealthPanel project={project} onProjectChanged={onProjectChanged} />
        </div>
      </div>

      <UpcomingActivityPanel project={project} />

      <MilestonesSummary project={project} />

      <TravelInfoPanel project={project} onSaved={onProjectChanged} />
    </div>
  );
}

const PROJECT_INFO_FIELD_STYLE = {
  label: { fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-mid)', marginBottom:3 },
  value: { color:'var(--heading)' },
};

function InfoField({ label, value, span2 }) {
  return (
    <div style={{ gridColumn: span2 ? '1 / -1' : undefined }}>
      <div style={PROJECT_INFO_FIELD_STYLE.label}>{label}</div>
      <div style={PROJECT_INFO_FIELD_STYLE.value}>{value}</div>
    </div>
  );
}

function projectInfoFormData(p) {
  return {
    name: p.name,
    accountId: p.accountId || '',
    productName: p.productName || '',
    projectStatus: p.projectStatus,
    startDate: p.startDate ? p.startDate.slice(0, 10) : '',
    targetEndDate: p.targetEndDate ? p.targetEndDate.slice(0, 10) : '',
    softwareModules: p.sowDetails?.softwareModules || '',
  };
}

// ── PROJECT INFO (compact identity card, Project Details hub) ──────────────
// Everything a PM needs to confirm about a project at a glance, in one card with a single Edit
// toggle -- ProjectDetailsPanel/AssignTeamPanel (Project Setup, below) keep the same fields spread
// across two forms for the one-time launch checklist; this is the ongoing quick-reference/edit.
function ProjectInfoPanel({ project, onSaved }) {
  const [isEditing, setIsEditing] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [members, setMembers] = useState([]);
  const [moduleLeads, setModuleLeads] = useState({});
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [data, setData] = useState(() => projectInfoFormData(project));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [users, leads] = await Promise.all([
          apiFetch('/users'),
          apiFetch(`/projects/${project.id}/module-leads`),
        ]);
        if (cancelled) return;
        setMembers(users.filter((u) => u.isActive).slice().sort((a, b) => a.name.localeCompare(b.name)));
        setModuleLeads(Object.fromEntries(leads.map((l) => [l.moduleName, l.userId])));
      } catch {
        // Non-fatal -- the display view just falls back to "— unassigned —" for every module.
      } finally {
        if (!cancelled) setLoadingLeads(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  useEffect(() => {
    if (isEditing) apiFetch('/accounts').then(setAccounts).catch(() => setAccounts([]));
  }, [isEditing]);

  useEffect(() => { setData(projectInfoFormData(project)); }, [project]);

  function startEdit() {
    setData(projectInfoFormData(project));
    setError('');
    setIsEditing(true);
  }

  function set(key) {
    return (e) => setData((prev) => ({ ...prev, [key]: e.target.value }));
  }

  const editModules = (data.softwareModules || '').split('\n').map((m) => m.trim()).filter(Boolean);
  const displayModules = (project.sowDetails?.softwareModules || '').split('\n').map((m) => m.trim()).filter(Boolean);
  const leadName = (moduleName) => members.find((m) => m.id === moduleLeads[moduleName])?.name || '— unassigned —';

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: data.name,
          accountId: data.accountId || undefined,
          productName: data.productName || undefined,
          projectStatus: data.projectStatus,
          startDate: data.startDate || undefined,
          targetEndDate: data.targetEndDate || undefined,
        }),
      });
      await apiFetch(`/projects/${project.id}/sow-details`, {
        method: 'PATCH',
        body: JSON.stringify({ softwareModules: data.softwareModules }),
      });
      await apiFetch(`/projects/${project.id}/module-leads`, {
        method: 'PUT',
        body: JSON.stringify({
          moduleLeads: Object.entries(moduleLeads)
            .filter(([moduleName, userId]) => userId && editModules.includes(moduleName))
            .map(([moduleName, userId]) => ({ moduleName, userId })),
        }),
      });
      setSavedAt(new Date());
      setIsEditing(false);
      await onSaved?.();
    } catch (err) {
      setError('Could not save project info — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  const actions = (
    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
      {/* Jump links to the two frames below the fold (Jeremy, 2026-09-10) -- small, in the one card
          that's always open at the top of the hub. */}
      <a href="#milestones-frame">↓ Milestones</a>
      <a href="#travel-info-frame">↓ Travel Info</a>
      <button
        className="btn btn-outline"
        style={{ fontSize:12, padding:'4px 10px' }}
        onClick={() => (isEditing ? setIsEditing(false) : startEdit())}
      >
        {isEditing ? 'Cancel' : 'Edit'}
      </button>
    </div>
  );

  return (
    <CollapsibleCard title="Project Info" actions={actions}>
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}

      {!isEditing ? (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, fontSize:13, marginBottom: displayModules.length ? 14 : 0 }}>
            <InfoField label="Project Name" value={project.name} />
            <InfoField label="Customer" value={project.account?.name || '—'} />
            <InfoField label="Product Name" value={project.productName || '—'} />
            <InfoField label="Project Status" value={<StatusBadge status={project.projectStatus} />} />
            <InfoField label="Start Date" value={project.startDate ? project.startDate.slice(0, 10) : '—'} />
            <InfoField label="Target End Date" value={project.targetEndDate ? project.targetEndDate.slice(0, 10) : '—'} />
          </div>
          {displayModules.length > 0 && (
            <div>
              <div style={PROJECT_INFO_FIELD_STYLE.label}>Modules &amp; Leads</div>
              <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                    <th style={{ padding:'4px 8px' }}>Module</th>
                    <th style={{ padding:'4px 8px' }}>Lead</th>
                  </tr>
                </thead>
                <tbody>
                  {displayModules.map((m) => (
                    <tr key={m} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'4px 8px' }}>{m}</td>
                      <td style={{ padding:'4px 8px' }}>{loadingLeads ? 'Loading…' : leadName(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Project Name</label>
              <input type="text" className="form-input" value={data.name} onChange={set('name')} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Customer</label>
              <select className="form-select" value={data.accountId} onChange={set('accountId')}>
                <option value="">— Account —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Product Name</label>
              <input type="text" className="form-input" value={data.productName} onChange={set('productName')} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Project Status</label>
              <select className="form-select" value={data.projectStatus} onChange={set('projectStatus')}>
                {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Start Date</label>
              <input type="date" className="form-input" value={data.startDate} onChange={set('startDate')} />
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">Target End Date</label>
              <input type="date" className="form-input" value={data.targetEndDate} onChange={set('targetEndDate')} />
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <ModuleScopePicker
              value={data.softwareModules}
              onChange={(v) => setData((prev) => ({ ...prev, softwareModules: v }))}
            />
          </div>

          {editModules.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-mid)', marginBottom:12 }}>
                Module Leads
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
                {editModules.map((module) => (
                  <div key={module} className="form-group" style={{ marginBottom:0 }}>
                    <label className="form-label">{module} Lead</label>
                    <select
                      className="form-select" disabled={loadingLeads}
                      value={moduleLeads[module] || ''}
                      onChange={(e) => setModuleLeads((prev) => ({ ...prev, [module]: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            {savedAt && <span style={{ fontSize:12, color:'var(--green)' }}>✓ Saved</span>}
          </div>
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── HEALTH / "KPIs" CARD (Project Details frame, redesigned 2026-09-10, retitled 2026-09-11) ───
// Five rows, all stacked and rated the same way: Budget/Scope/Schedule/Client, plus Overall. The card
// itself is titled "KPIs" (Jeremy) -- unrelated to the milestone-stats "Milestone summary" below.
// Overall used to be a computed rollup with its own separate log-update flow; it's now a direct
// edit like the other four, with the same inline comment field -- the only thing that's still
// special about it is that its comment has no plain column (unlike the other four's ...Comment
// fields), so it's logged as a fresh StatusReportSnapshot history entry on every save instead of
// overwritten in place. History is collapsed by default (Jeremy, 2026-09-10) -- most saves don't
// need it, and it's one click away.
const HEALTH_CATEGORIES = [
  ['budgetHealth', 'budgetHealthComment', 'Budget'],
  ['scopeHealth', 'scopeHealthComment', 'Scope'],
  ['scheduleHealth', 'scheduleHealthComment', 'Schedule'],
  ['clientHealth', 'clientHealthComment', 'Client'],
  ['ragStatus', 'overallComment', 'Overall'],
];

function healthFormData(p) {
  return {
    budgetHealth: p.budgetHealth || '',
    scopeHealth: p.scopeHealth || '',
    scheduleHealth: p.scheduleHealth || '',
    clientHealth: p.clientHealth || '',
    ragStatus: p.ragStatus || '',
    budgetHealthComment: p.budgetHealthComment || '',
    scopeHealthComment: p.scopeHealthComment || '',
    scheduleHealthComment: p.scheduleHealthComment || '',
    clientHealthComment: p.clientHealthComment || '',
  };
}

function HealthPanel({ project, onProjectChanged }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [ratings, setRatings] = useState(() => ({ ...healthFormData(project), overallComment: '' }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  async function loadHistory() {
    try {
      setHistory(await apiFetch(`/projects/${project.id}/status-snapshots`));
    } catch (err) {
      setError('Could not load health history — ' + err.message);
    } finally {
      setLoadingHistory(false);
    }
  }

  useEffect(() => {
    setLoadingHistory(true);
    setError('');
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  // Keeps the selects/comments in step if the project reloads with ratings set some other way.
  // overallComment has no server-side "current value" to resync from -- only reset it here when
  // the project itself changed underneath (a fresh mount), not on every field update.
  useEffect(() => {
    setRatings((prev) => ({ ...healthFormData(project), overallComment: prev.overallComment }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project.budgetHealth, project.scopeHealth, project.scheduleHealth, project.clientHealth, project.ragStatus,
    project.budgetHealthComment, project.scopeHealthComment, project.scheduleHealthComment, project.clientHealthComment,
  ]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          budgetHealth: ratings.budgetHealth || undefined,
          scopeHealth: ratings.scopeHealth || undefined,
          scheduleHealth: ratings.scheduleHealth || undefined,
          clientHealth: ratings.clientHealth || undefined,
          ragStatus: ratings.ragStatus || undefined,
          budgetHealthComment: ratings.budgetHealthComment,
          scopeHealthComment: ratings.scopeHealthComment,
          scheduleHealthComment: ratings.scheduleHealthComment,
          clientHealthComment: ratings.clientHealthComment,
        }),
      });
      // Overall's comment isn't a plain field -- it always becomes a fresh history entry instead,
      // stamped with whatever Overall rating was just saved above (ProjectsService reads it fresh,
      // not trusting a client-sent value).
      if (ratings.ragStatus) {
        await apiFetch(`/projects/${project.id}/status-snapshots`, {
          method: 'POST',
          body: JSON.stringify({ narrative: ratings.overallComment.trim() || undefined }),
        });
      }
      setRatings((prev) => ({ ...prev, overallComment: '' }));
      setSavedAt(new Date());
      await Promise.all([loadHistory(), onProjectChanged?.()]);
    } catch (err) {
      setError('Could not save health — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    // Titled "KPIs" (Jeremy, 2026-09-11) -- the milestone-stats card that used to hold that name is
    // now "Milestone summary", folded into the Milestones frame below.
    <CollapsibleCard title="KPIs">
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
        {HEALTH_CATEGORIES.map(([key, commentKey, label]) => (
          <div key={key} style={{ display:'flex', alignItems:'center', gap:6 }}>
            <label style={{ fontSize:12, color:'var(--text-mid)', width:56, flexShrink:0 }}>{label}</label>
            <select
              className="form-select" style={{ width:84, flexShrink:0, padding:'4px 6px' }}
              value={ratings[key]}
              onChange={(e) => setRatings((prev) => ({ ...prev, [key]: e.target.value }))}
            >
              <option value="">— Not set —</option>
              {RAG_STATUS_VALUES.map((v) => <option key={v}>{v}</option>)}
            </select>
            {ratings[key] && <span className={`badge ${RAG_BADGE_CLASS[ratings[key]]}`} style={{ flexShrink:0 }}>{ratings[key]}</span>}
            <input
              type="text" className="form-input" style={{ flex:1, minWidth:0 }}
              placeholder="Comment (optional)"
              value={ratings[commentKey]}
              onChange={(e) => setRatings((prev) => ({ ...prev, [commentKey]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save health'}
        </button>
        {savedAt && <span style={{ fontSize:12, color:'var(--green)' }}>✓ Saved</span>}
      </div>

      <button
        className="btn btn-outline" style={{ fontSize:11, padding:'3px 8px' }}
        onClick={() => setShowHistory((s) => !s)}
      >
        {showHistory ? 'Hide history' : 'Show history'}
      </button>
      {showHistory && (
        <div style={{ marginTop:10, paddingTop:10, borderTop:'1px solid var(--border)' }}>
          {loadingHistory ? (
            <div style={{ fontSize:12, color:'var(--text-mid)' }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ fontSize:12, color:'var(--text-mid)' }}>No health updates logged yet.</div>
          ) : (
            <div style={{ maxHeight:160, overflowY:'auto', display:'flex', flexDirection:'column', gap:8 }}>
              {history.map((h) => (
                <div key={h.id} style={{ fontSize:12, borderBottom:'1px solid var(--border)', paddingBottom:6 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
                    <span className={`badge ${RAG_BADGE_CLASS[h.ragStatus]}`}>{h.ragStatus}</span>
                    <span style={{ color:'var(--text-mid)' }}>{h.snapshotDate.slice(0, 10)}</span>
                  </div>
                  {h.narrative && <div style={{ color:'var(--text-mid)' }}>{h.narrative}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── BUDGET (Project Details frame) ──────────────────────────
// A summary only -- managing individual budget lines by team happens on the full Project Budgets
// page (item 6), linked here with the standard ?project= convention (ADR 0038).
function BudgetSummaryPanel({ project }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch(`/projects/${project.id}/budget-summary`)
      .then((r) => { if (!cancelled) setSummary(r); })
      .catch((err) => { if (!cancelled) setError('Could not load budget — ' + err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project.id]);

  const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits:0 })}`;
  const fmtHours = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits:0 })}h`;
  const isEmpty = summary && summary.byTeam.length === 0 && summary.projectLevelLines.length === 0;

  return (
    <CollapsibleCard
      title="Budget"
      actions={<Link to={projectPath('/project-budgets', project.id)}>Manage →</Link>}
    >
      <div className="proxy-banner" style={{ marginBottom:10, fontSize:12, padding:'6px 10px' }}>
        ⚠ Work in progress — data not yet reliable.
      </div>
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}
      {loading ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>Loading…</div>
      ) : isEmpty ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>No budget lines yet.</div>
      ) : (
        <div style={{ fontSize:13 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <span style={{ color:'var(--text-mid)' }}>Planned</span>
            <span>{fmtMoney(summary.projectTotal.plannedAmount)} · {fmtHours(summary.projectTotal.plannedHours)}</span>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
            <span style={{ color:'var(--text-mid)' }}>Actual</span>
            <span>{fmtMoney(summary.projectTotal.actualAmount)} · {fmtHours(summary.projectTotal.actualHours)}</span>
          </div>
          {summary.byTeam.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:120, overflowY:'auto' }}>
              {summary.byTeam.map((t) => (
                <div key={t.teamId} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text-mid)' }}>
                  <span>{t.teamName}</span>
                  <span>{fmtHours(t.actualHours)} / {fmtHours(t.plannedHours)} planned</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── NEXT 30/60/90 (Project Details hub, 2026-09-10) ─────────
// Reuses the `calendar` module's own cross-type event feed (GET /calendar/events/:projectId) --
// the same milestone/phase/task/BPA/RAAIDD/change/BAU union Project Calendar already computes,
// just bucketed by days-out instead of laid out on a calendar grid. No API change needed.
const ACTIVITY_BUCKETS = [
  ['next30', 'Next 30 Days', 0, 30],
  ['next60', 'Next 31–60 Days', 31, 60],
  ['next90', 'Next 61–90 Days', 61, 90],
];

function UpcomingActivityPanel({ project }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);
  const [overdueItems, setOverdueItems] = useState([]);

  const loadEvents = useCallback(() => {
    setLoading(true);
    // The calendar feed only carries dated rows, so open RAAIDD items with no due date (or one in the
    // past) never reached these cards (Deborah Lukens, 2026-09-21). Read them from the register itself.
    const todayMs = new Date().setHours(0, 0, 0, 0);
    apiFetch(`/risk-register-items?projectId=${project.id}`)
      .then((items) => setOverdueItems(items.filter((i) =>
        !['Completed', 'Closed', 'Canceled'].includes(i.status)
        && (!i.dateDue || new Date(i.dateDue).setHours(0, 0, 0, 0) < todayMs))))
      .catch(() => setOverdueItems([]));
    apiFetch(`/calendar/events/${project.id}`)
      .then((r) => setEvents(r))
      .catch((err) => setError('Could not load upcoming activity — ' + err.message))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const today = new Date().setHours(0, 0, 0, 0);
  const daysOut = (e) => Math.round((new Date(e.start).setHours(0, 0, 0, 0) - today) / 86400000);

  return (
    <CollapsibleCard
      title="Next 30 / 60 / 90"
      actions={<button className="btn btn-outline" style={{ fontSize:12, padding:'4px 10px' }} onClick={() => setAddingEvent(true)}>+ Add Project Event</button>}
    >
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}
      {addingEvent && (
        <ProjectEventModal
          projects={[{ id: project.id, name: project.name }]}
          initialProjectId={project.id}
          onClose={() => setAddingEvent(false)}
          onSaved={loadEvents}
        />
      )}
      {loading ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>Loading…</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:16 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--red)', marginBottom:8 }}>
              Past due / no date <span style={{ fontWeight:400 }}>({overdueItems.length})</span>
            </div>
            {overdueItems.length === 0 ? (
              <div style={{ fontSize:12, color:'var(--text-mid)' }}>Nothing overdue.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
                {overdueItems.map((i) => (
                  <Link key={i.id} to={`/registers?tab=raaidd&project=${project.id}`} style={{ fontSize:12 }}>
                    <div style={{ color:'var(--heading)' }}>{i.type} #{i.logNumber}: {i.description}</div>
                    <div style={{ color:'var(--text-mid)' }}>{i.dateDue ? `Due ${new Date(i.dateDue).toLocaleDateString()}` : 'No due date'}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {ACTIVITY_BUCKETS.map(([key, label, min, max]) => {
            const bucketed = events
              .filter((e) => { const d = daysOut(e); return d >= min && d <= max; })
              .sort((a, b) => new Date(a.start) - new Date(b.start));
            return (
              <div key={key}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-mid)', marginBottom:8 }}>
                  {label} <span style={{ fontWeight:400 }}>({bucketed.length})</span>
                </div>
                {bucketed.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--text-mid)' }}>Nothing scheduled.</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:220, overflowY:'auto' }}>
                    {bucketed.map((e) => (
                      <Link key={e.id} to={e.link} style={{ fontSize:12 }}>
                        <div style={{ color:'var(--heading)' }}>{e.title}</div>
                        <div style={{ color:'var(--text-mid)' }}>{new Date(e.start).toLocaleDateString()}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── MILESTONES (full-detail read-only table, Project Details hub) ──────────
// Read-only -- full add/edit/delete lives on Project Setup (MilestonesSection below), reached via
// the "Manage milestones" link. Shows every business field on the Milestone model (Jeremy asked
// to see them all before trimming) except raw ids, the AI-extraction audit trail
// (rawSourceText/legacyOnePlanId) and row metadata (createdAt/updatedAt) -- none of those are
// something a PM reads off this table. Status/badge convention duplicated from
// MilestoneTracker.js (itself duplicated from packages/shared per ADR 0019).
// Matches packages/shared's MILESTONE_STATUSES (apps/web can't import it, ADR 0019) -- this map had
// drifted from it (missing Billed/Paid, a stale 'Missed' that was never a real status) since before
// this diff; fixed here since the new Setup grid's status editor now makes Billed/Paid reachable from
// this file's own UI for the first time.
const MILESTONE_STATUS_BADGE_CLASS = {
  'Not Started': 'badge-blue',
  'In Progress': 'badge-blue',
  Delivered: 'badge-green',
  Accepted: 'badge-green',
  Billed: 'badge-green',
  Paid: 'badge-green',
};

function fmtDate(d) {
  return d ? d.slice(0, 10) : '—';
}
function fmtMilestoneAmount(n) {
  return n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits:0 })}`;
}

// One definition per column drives the header, the cell, and what `sortRows` compares -- adding a
// column later means one new entry here, not three separate places to keep in sync. `value()` is
// what gets compared when sorting; empty/missing sorts first ascending, same convention `sortRows`
// callers elsewhere in this file already use. No Phase/Workstream here -- Project Setup's grid is the
// only one that shows them (Jeremy, 2026-09-11), since assigning either needs the id-bearing pickers
// that live there. Go-Live is a badge on the title, not its own column, for the same reason.
const MILESTONE_COLUMNS = [
  { key: 'sequence', label: 'Seq', value: (m) => m.sequence ?? -1, render: (m) => m.sequence ?? '—' },
  { key: 'sowMilestoneNumber', label: '#', value: (m) => m.sowMilestoneNumber || '', render: (m) => m.sowMilestoneNumber || '—' },
  { key: 'dasNumber', label: 'DAS #', value: (m) => m.dasNumber || '', render: (m) => m.dasNumber || '—' },
  {
    key: 'title', label: 'Milestone', value: (m) => lower(m.title),
    render: (m) => (
      <>
        {m.title}
        {m.isGoLive && <span className="badge badge-green" style={{ marginLeft:6, fontSize:10 }} title="Go-live milestone">Go-Live</span>}
      </>
    ),
  },
  { key: 'amount', label: 'Amount', value: (m) => Number(m.amount ?? -1), render: (m) => fmtMilestoneAmount(m.amount) },
  {
    key: 'billingCategory', label: 'Category', value: (m) => m.billingCategory || 'zz',
    render: (m) => (m.billingCategory
      ? <span className="badge badge-blue" style={{ fontSize:10 }}>{m.billingCategory}</span>
      : '—'),
  },
  {
    // Committed / Draw-down / Optional -- whether the money bills at all, as against what kind of
    // work it is. Only the contingent ones are worth a badge; Committed is the norm.
    key: 'valueType', label: 'Value', value: (m) => m.valueType || 'Committed',
    render: (m) => ((m.valueType && m.valueType !== 'Committed')
      ? <span className="badge badge-yellow" style={{ fontSize:10 }}>{m.valueType}</span>
      : 'Committed'),
  },
  {
    // A recurring line is one row by rule (Milestone.recurrence in schema.prisma); Target is its
    // first period and this says where it ends and how far along it is.
    key: 'recurrence', label: 'Repeats', value: (m) => m.recurrence || '',
    render: (m) => (m.recurrence
      ? `${m.recurrence} to ${fmtDate(m.recurrenceEnd)} · ${m.periodsCompleted || 0} done`
      : '—'),
  },
  {
    key: 'status', label: 'Status', value: (m) => lower(m.status),
    render: (m) => <span className={`badge ${MILESTONE_STATUS_BADGE_CLASS[m.status] || 'badge-blue'}`}>{m.status || '—'}</span>,
  },
  { key: 'baselineDate', label: 'Baseline', value: (m) => m.baselineDate || '', render: (m) => fmtDate(m.baselineDate) },
  { key: 'targetDate', label: 'Target', value: (m) => m.targetDate || '', render: (m) => fmtDate(m.targetDate) },
  { key: 'actualDate', label: 'Actual', value: (m) => m.actualDate || '', render: (m) => fmtDate(m.actualDate) },
  { key: 'dateSubmitted', label: 'Submitted', value: (m) => m.dateSubmitted || '', render: (m) => fmtDate(m.dateSubmitted) },
  { key: 'dateSigned', label: 'Signed', value: (m) => m.dateSigned || '', render: (m) => fmtDate(m.dateSigned) },
  { key: 'dateInvoiced', label: 'Invoiced', value: (m) => m.dateInvoiced || '', render: (m) => fmtDate(m.dateInvoiced) },
  { key: 'datePaid', label: 'Paid', value: (m) => m.datePaid || '', render: (m) => fmtDate(m.datePaid) },
  { key: 'acceptanceCriteria', label: 'Acceptance Criteria', value: (m) => lower(m.acceptanceCriteria), render: (m) => m.acceptanceCriteria || '—' },
];
const MILESTONE_COLUMN_KEYS = MILESTONE_COLUMNS.map((c) => c.key);

// Draggable + sortable in one header cell -- native HTML5 DnD rather than a library (nothing else
// in this app pulls one in for drag-and-drop), click-to-sort coexists with it since a plain click
// (no pointer movement) never fires dragstart. Sticky (Jeremy, 2026-09-10): the wrapper below caps
// the table's height and scrolls, so a solid background is required here -- without it, a sticky
// cell is transparent and rows scroll up visibly through the "frozen" header.
function MilestoneColumnHeader({ col, sort, onSort, draggedKey, onDragStart, onDragOver, onDrop }) {
  const active = sort.key === col.key;
  return (
    <th
      className="sortable"
      draggable
      onDragStart={() => onDragStart(col.key)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); onDrop(col.key); }}
      onClick={() => onSort(col.key)}
      title="Click to sort, drag to reorder columns"
      style={{
        padding:'6px 10px', cursor:'grab', opacity: draggedKey === col.key ? 0.4 : 1,
        position:'sticky', top:0, zIndex:1, background:'var(--light-bg)',
      }}
    >
      {col.label}
      <span className={`sort-arrow${active ? '' : ' is-idle'}`}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

// Mirrors ProjectSchedule.js's own MILESTONE_COMPLETE set (apps/web can't import packages/shared,
// ADR 0019). Same classification the backend's kpis() tiles use, reproduced here so the "Milestone
// summary" tile counts and the filter they drive on the table below can never disagree -- both read
// off this one function over the same already-loaded `milestones` array, not two separate sources.
const MILESTONE_DONE_STATUSES = new Set(['Delivered', 'Accepted', 'Billed', 'Paid']);

function classifyMilestone(m) {
  if (m.status && MILESTONE_DONE_STATUSES.has(m.status)) return 'complete';
  if (m.targetDate && new Date(m.targetDate) < new Date()) return 'overdue';
  return 'upcoming';
}

// "Milestone summary" (2026-09-11) -- this used to be the standalone "KPIs" card; Jeremy asked for it
// renamed and folded into the Milestones frame, with the three count tiles doubling as click-to-
// filter buttons on the table below. % Complete stays informational (task-based, not a milestone
// bucket) and comes from GET /projects/:id/kpis rather than being derivable from `milestones` alone.
function MilestoneSummaryTiles({ counts, percentComplete, nextMilestone, activeFilter, onToggle }) {
  const tile = (key, label, value, clickable) => (
    <div
      key={key}
      onClick={clickable ? () => onToggle(key) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      style={{
        padding: '10px 12px', borderRadius: 8, cursor: clickable ? 'pointer' : 'default',
        border: activeFilter === key ? '1px solid var(--primary-dark)' : '1px solid var(--border)',
        background: activeFilter === key ? 'var(--primary-light)' : 'transparent',
      }}
      title={clickable ? 'Click to filter the table below' : undefined}
    >
      <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--heading)' }}>{value}</div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 8 }}>
        Milestone summary
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
        {tile('percentComplete', '% Complete', percentComplete == null ? '—' : `${percentComplete}%`, false)}
        {tile('complete', 'Milestones Complete', counts.complete, true)}
        {tile('overdue', 'Milestones Overdue', counts.overdue, true)}
        {tile('upcoming', 'Milestones Upcoming', counts.upcoming, true)}
        {tile('nextMilestone', 'Next Milestone', nextMilestone ?? '—', false)}
      </div>
      {activeFilter && (
        <div style={{ marginBottom: 12 }}>
          <button className="btn btn-outline" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => onToggle(activeFilter)}>
            Clear filter
          </button>
        </div>
      )}
    </div>
  );
}

function MilestonesSummary({ project }) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [percentComplete, setPercentComplete] = useState(null);
  const [sort, toggleSort] = useGridSort('sequence');
  const [columnOrder, setColumnOrder] = useState(MILESTONE_COLUMN_KEYS);
  const [draggedKey, setDraggedKey] = useState(null);
  // null | 'complete' | 'overdue' | 'upcoming' -- which summary tile the table is filtered to.
  const [activeFilter, setActiveFilter] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`/milestones?projectId=${project.id}`)
      .then((r) => { if (!cancelled) setMilestones(r); })
      .catch((err) => { if (!cancelled) setError('Could not load milestones — ' + err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // % Complete is task-based, not derivable from the milestones list -- a small separate call
    // rather than pulling every Task down to this page just for one number.
    apiFetch(`/projects/${project.id}/kpis`)
      .then((r) => {
        if (cancelled) return;
        const t = r.tiles.find((tile) => tile.key === 'percentComplete');
        setPercentComplete(t ? t.value : null);
      })
      .catch(() => { /* Non-fatal -- the tile just reads "—". */ });
    return () => { cancelled = true; };
  }, [project.id]);

  function reorderColumns(targetKey) {
    if (!draggedKey || draggedKey === targetKey) { setDraggedKey(null); return; }
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== draggedKey);
      next.splice(next.indexOf(targetKey), 0, draggedKey);
      return next;
    });
    setDraggedKey(null);
  }

  const counts = { complete: 0, overdue: 0, upcoming: 0 };
  let nextMilestone = null;
  for (const m of milestones) {
    const bucket = classifyMilestone(m);
    counts[bucket] += 1;
    // Only an upcoming (not-yet-due, not-done) milestone can be "next" -- same reasoning as the
    // backend's kpis(): an overdue one earlier in the schedule would otherwise win the comparison.
    if (bucket === 'upcoming' && m.targetDate && (!nextMilestone || m.targetDate < nextMilestone.targetDate)) {
      nextMilestone = m;
    }
  }
  const nextMilestoneLabel = nextMilestone ? `${nextMilestone.title} — ${fmtDate(nextMilestone.targetDate)}` : null;

  const columns = columnOrder.map((key) => MILESTONE_COLUMNS.find((c) => c.key === key));
  const filteredMilestones = activeFilter ? milestones.filter((m) => classifyMilestone(m) === activeFilter) : milestones;
  const sorted = sortRows(filteredMilestones, sort, (row, key) => MILESTONE_COLUMNS.find((c) => c.key === key).value(row));

  return (
    <CollapsibleCard
      id="milestones-frame"
      title="Milestones"
      actions={<Link to="/project-launch?view=setup">Manage milestones →</Link>}
    >
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}
      {!loading && !!milestones.length && (
        <MilestoneSummaryTiles
          counts={counts}
          percentComplete={percentComplete}
          nextMilestone={nextMilestoneLabel}
          activeFilter={activeFilter}
          onToggle={(key) => setActiveFilter((prev) => (prev === key ? null : key))}
        />
      )}
      {loading ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>Loading…</div>
      ) : milestones.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>No milestones yet.</div>
      ) : sorted.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>No milestones match this filter.</div>
      ) : (
        <div style={{ overflow:'auto', maxHeight:480 }}>
          <table className="sheet" style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
            <thead>
              <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                {columns.map((col) => (
                  <MilestoneColumnHeader
                    key={col.key} col={col} sort={sort} onSort={toggleSort}
                    draggedKey={draggedKey} onDragStart={setDraggedKey} onDrop={reorderColumns}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((m) => (
                <tr key={m.id} style={{ borderTop:'1px solid var(--border)' }}>
                  {columns.map((col) => (
                    <td key={col.key} style={{ padding:'6px 10px' }}>{col.render(m)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
}

// ── TRAVEL INFO (Project Details hub, 2026-09-10) ───────────
// Always-editable, like Health's narrative field -- no display/edit toggle needed for a notes
// frame that's meant to be updated in place.
// Five boxes (2026-09-11, Jeremy; was Airport/Hotel/Notes) -- `field` is the value POSTed to
// /projects/:id/travel-attachments and must match TRAVEL_ATTACHMENT_FIELDS on the API.
const TRAVEL_FIELDS = [
  { field: 'airport', stateKey: 'travelAirport', label: 'Airport' },
  { field: 'hotel', stateKey: 'travelHotel', label: 'Hotel' },
  { field: 'perDiem', stateKey: 'travelPerDiem', label: 'Per Diem' },
  { field: 'expenseReporting', stateKey: 'travelExpenseReporting', label: 'Expense Reporting' },
  { field: 'notes', stateKey: 'travelNotes', label: 'Other Travel Notes' },
];

function travelFormData(p) {
  return Object.fromEntries(TRAVEL_FIELDS.map((f) => [f.field, p[f.stateKey] || '']));
}

// A pasted screenshot per box (2026-09-11) -- this is the app's first image-paste UI anywhere, so it
// only reads image/* items off the clipboard (no file picker, no drag-drop) and uploads immediately
// rather than staging it as part of the text Save below; an attachment is its own record either way.
function TravelAttachments({ items, onRemove, onView, uploading }) {
  return (
    <div style={{ marginTop: 8 }}>
      {uploading && <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 6 }}>Uploading image…</div>}
      {!!items.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {items.map((a) => (
            <div key={a.id} style={{ position: 'relative' }}>
              <img
                src={a.dataUrl} alt="Pasted attachment"
                onClick={() => onView(a)}
                style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', cursor: 'zoom-in' }}
              />
              <button
                onClick={() => onRemove(a)}
                title="Remove image"
                style={{
                  position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%',
                  border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer',
                  fontSize: 10, lineHeight: '16px', padding: 0,
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
        Paste a screenshot into the box above to attach it here.
      </div>
    </div>
  );
}

function TravelInfoPanel({ project, onSaved }) {
  const [data, setData] = useState(() => travelFormData(project));
  const [attachments, setAttachments] = useState([]);
  const [uploadingField, setUploadingField] = useState(null);
  const [viewing, setViewing] = useState(null); // the attachment shown full-size, or null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => { setData(travelFormData(project)); }, [project]);

  const loadAttachments = useCallback(() => {
    apiFetch(`/projects/${project.id}/travel-attachments`)
      .then(setAttachments)
      .catch(() => { /* Non-fatal -- the boxes still work without their images loading. */ });
  }, [project.id]);

  useEffect(() => { loadAttachments(); }, [loadAttachments]);

  async function handlePaste(field, e) {
    const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
    if (!item) return; // Plain text paste -- let the browser handle it normally.
    e.preventDefault();
    const file = item.getAsFile();
    if (!file) return;
    setUploadingField(field);
    setError('');
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
      await apiFetch(`/projects/${project.id}/travel-attachments`, {
        method: 'POST',
        body: JSON.stringify({ field, imageData: base64, mimeType: file.type }),
      });
      loadAttachments();
    } catch (err) {
      setError('Could not attach image — ' + err.message);
    } finally {
      setUploadingField(null);
    }
  }

  async function removeAttachment(attachment) {
    try {
      await apiFetch(`/projects/${project.id}/travel-attachments/${attachment.id}`, { method: 'DELETE' });
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
      setViewing((prev) => (prev?.id === attachment.id ? null : prev));
    } catch (err) {
      setError('Could not remove image — ' + err.message);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          travelAirport: data.airport,
          travelHotel: data.hotel,
          travelPerDiem: data.perDiem,
          travelExpenseReporting: data.expenseReporting,
          travelNotes: data.notes,
        }),
      });
      setSavedAt(new Date());
      await onSaved?.();
    } catch (err) {
      setError('Could not save travel information — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  const attachmentsByField = (field) => attachments.filter((a) => a.field === field);

  return (
    <CollapsibleCard id="travel-info-frame" title="Travel Information">
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        {TRAVEL_FIELDS.map((f) => (
          <div className="form-group" style={{ marginBottom:0 }} key={f.field}>
            <label className="form-label">{f.label}</label>
            <textarea
              className="form-input" rows={f.field === 'notes' ? 3 : 2}
              value={data[f.field]}
              onChange={(e) => setData((prev) => ({ ...prev, [f.field]: e.target.value }))}
              onPaste={(e) => handlePaste(f.field, e)}
            />
            <TravelAttachments
              items={attachmentsByField(f.field)}
              onRemove={removeAttachment}
              onView={setViewing}
              uploading={uploadingField === f.field}
            />
          </div>
        ))}
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save travel info'}
        </button>
        {savedAt && <span style={{ fontSize:12, color:'var(--green)' }}>✓ Saved</span>}
      </div>

      {viewing && (
        <>
          <div onClick={() => setViewing(null)} style={{ position:'fixed', inset:0, zIndex:50, background:'rgba(0,0,0,0.6)' }} aria-hidden />
          <img
            src={viewing.dataUrl} alt="Attachment, full size"
            onClick={() => setViewing(null)}
            style={{
              position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', zIndex:51,
              maxWidth:'90vw', maxHeight:'90vh', borderRadius:8, cursor:'zoom-out',
              boxShadow:'0 10px 34px rgba(0,0,0,0.4)',
            }}
          />
        </>
      )}
    </CollapsibleCard>
  );
}

// ── PROJECT SETUP DETAIL (checklist hub + the old ProjectSetup wizard, merged) ──────────────
function ProjectSetupDetail({ project, isAdmin, onProjectChanged }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Loading a SOW against this placeholder takes over the whole detail view (item 7) rather than
  // sitting alongside the sections below -- the same full-screen feel the standalone tab had, just
  // scoped to one placeholder instead of picking from all of them.
  const [showIntake, setShowIntake] = useState(false);
  // Categories fully resolved (Complete/N/A) start collapsed -- item 9's compactness ask -- so a
  // project well into setup isn't a wall of checked-off rows. Computed once per project load, not
  // on every item edit (a ref, not a dependency-driven effect), so checking a box mid-category
  // doesn't yank the section shut around the user's own click.
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const collapseInitialized = useRef(false);

  async function reload() {
    try {
      const data = await apiFetch(`/projects/${project.id}/checklist`);
      setChecklist(data);
      if (!collapseInitialized.current) {
        collapseInitialized.current = true;
        const byCat = {};
        for (const item of data.items || []) (byCat[item.category] ||= []).push(item);
        setCollapsedCategories(new Set(
          Object.keys(byCat).filter(
            (c) => byCat[c].length > 0 && byCat[c].every((i) => i.status === 'Complete' || i.status === 'N/A'),
          ),
        ));
      }
    } catch (err) {
      setError('Could not load checklist — ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleCategory(category) {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  }

  useEffect(() => {
    setLoading(true);
    setError('');
    collapseInitialized.current = false;
    reload();
    // `reload` is a fresh closure every render (it closes over `project`); only re-run on a real
    // project change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  async function updateItem(itemId, patch) {
    const updated = await apiFetch(`/checklist-items/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) });
    setChecklist((prev) => ({ ...prev, items: prev.items.map((i) => (i.id === itemId ? updated : i)) }));
  }

  async function removeItem(itemId) {
    await apiFetch(`/checklist-items/${itemId}`, { method: 'DELETE' });
    setChecklist((prev) => ({ ...prev, items: prev.items.filter((i) => i.id !== itemId) }));
  }

  async function addItem(item) {
    const updated = await apiFetch(`/projects/${project.id}/checklist/items`, { method: 'POST', body: JSON.stringify(item) });
    setChecklist(updated);
  }

  if (loading) return <div style={{ textAlign:'center', padding:32, color:'var(--text-mid)', fontSize:13 }}>Loading…</div>;

  // Shared by the finish button and the back arrow: leaving after a commit via the back arrow used
  // to skip the refresh, so Project Setup kept showing the pre-SOW values.
  async function closeIntake() {
    setShowIntake(false);
    await onProjectChanged();
    await reload();
  }

  if (showIntake) {
    return (
      <div>
        <button className="btn btn-outline" style={{ marginBottom:16 }} onClick={closeIntake}>
          ← Back to project setup
        </button>
        <SOWIntake
          lockedPlaceholderId={project.id}
          existingProject={project}
          onDone={closeIntake}
        />
      </div>
    );
  }

  const items = checklist?.items || [];
  const byCategory = {};
  for (const item of items) (byCategory[item.category] ||= []).push(item);
  const categories = [
    ...CHECKLIST_CATEGORIES.filter((c) => byCategory[c]),
    ...Object.keys(byCategory).filter((c) => !CHECKLIST_CATEGORIES.includes(c)),
  ];

  return (
    <div>
      {error && (
        <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'rgba(239,65,54,0.08)',
          border:'1px solid rgba(239,65,54,0.3)', fontSize:13, color:'var(--red)' }}>
          ⚠ {error}
        </div>
      )}

      <PlaceholderActionsPanel
        project={project}
        isAdmin={isAdmin}
        onChanged={onProjectChanged}
        onLoadSow={() => setShowIntake(true)}
      />

      <ProjectDetailsPanel project={project} onSaved={onProjectChanged} />

      <AssignTeamPanel project={project} onSaved={onProjectChanged} />

      {/* Full add/edit/delete -- the Project Details hub only shows a compact read-only summary
          (MilestonesSummary), linking back here. */}
      <RecurringItSection project={project} />

      <MilestonesSection project={project} />

      <FolderStructureSection project={project} />

      <InitializeSection project={project} onChanged={onProjectChanged} />

      {categories.map((category) => {
        const catItems = byCategory[category].slice().sort((a, b) => a.sortOrder - b.sortOrder);
        const catResolved = catItems.filter((i) => i.status === 'Complete' || i.status === 'N/A').length;
        const isCollapsed = collapsedCategories.has(category);
        return (
          <div key={category} className="card" style={{ marginBottom:16 }}>
            <div
              className="card-title"
              style={{ cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
              onClick={() => toggleCategory(category)}
            >
              <span>{isCollapsed ? '▸' : '▾'} {category}</span>
              <span style={{ fontSize:12, fontWeight:400, color:'var(--text-mid)' }}>
                {catResolved} of {catItems.length}
              </span>
            </div>
            {!isCollapsed && (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {catItems.map((item) => (
                  <ChecklistItemRow
                    key={item.id}
                    item={item}
                    project={project}
                    onUpdate={(patch) => updateItem(item.id, patch)}
                    onRemove={() => removeItem(item.id)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <AddItemForm onAdd={addItem} />
    </div>
  );
}

// ── PLACEHOLDER ACTIONS ─────────────────────────────────────
// Only for a Placeholder that hasn't already been merged elsewhere -- an Active/Startup project has
// nothing left to promote, and an already-merged placeholder's forecast lives under the project it
// merged into. Loading a SOW is available to anyone who could reach the old standalone SOW Intake
// tab (it was never admin-gated); Promote is admin-only (Jeremy, 2026-09-07), matching
// promotePlaceholder's existing gate -- pinning a placeholder's identity as a real project is treated
// as more sensitive than filling in what it already knows about itself.
//
// "Merge into an existing project" used to live here too, but committing a SOW against this
// placeholder now does that same job with field-level conflict resolution instead of a blind merge
// (item 7) -- Project Configuration -> Projects & Accounts keeps its own bulk-cleanup merge action
// for the unrelated case of consolidating two already-real project rows.
function PlaceholderActionsPanel({ project, isAdmin, onChanged, onLoadSow }) {
  if (!project.isPlaceholder || project.mergedIntoProjectId) return null;

  return (
    <CollapsibleCard title="Placeholder actions">
      <div style={{ display:'grid', gridTemplateColumns: isAdmin ? '1fr 1fr' : '1fr', gap:16 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)', marginBottom:8 }}>
            Load a SOW
          </div>
          <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:10 }}>
            Extract a SOW and commit it against this placeholder. Anything already filled in here is
            merged with what the SOW says -- you'll be asked which value to keep only where the two
            actually conflict.
          </div>
          <button className="btn btn-primary" onClick={onLoadSow}>Load SOW →</button>
        </div>
        {isAdmin && <PromotePlaceholderForm project={project} onChanged={onChanged} />}
      </div>
    </CollapsibleCard>
  );
}

function PromotePlaceholderForm({ project, onChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [name, setName] = useState(project.name);
  const [accountId, setAccountId] = useState(project.accountId || '');
  const [productName, setProductName] = useState(project.productName || '');
  const [startDate, setStartDate] = useState(project.startDate ? project.startDate.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/accounts').then(setAccounts).catch(() => setAccounts([]));
  }, []);

  async function handlePromote() {
    if (!window.confirm(`Promote "${name}" to a real project? This can't be undone.`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}/promote-placeholder`, {
        method: 'POST',
        body: JSON.stringify({
          name: name || undefined,
          accountId: accountId || undefined,
          productName: productName || undefined,
          startDate: startDate || undefined,
        }),
      });
      await onChanged();
    } catch (err) {
      setError('Could not promote — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ fontSize:13, fontWeight:600, color:'var(--heading)', marginBottom:8 }}>
        Promote to a real project
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
        <input type="text" className="form-input" placeholder="Project name" value={name} onChange={(e) => setName(e.target.value)} />
        <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
          <option value="">— Account —</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <input type="text" className="form-input" placeholder="Product name" value={productName} onChange={(e) => setProductName(e.target.value)} />
        <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </div>
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:8 }}>⚠ {error}</div>}
      <button className="btn btn-primary" onClick={handlePromote} disabled={saving}>
        {saving ? 'Promoting…' : 'Promote'}
      </button>
    </div>
  );
}

// A module plus its own name and every descendant's, flattened -- used both to tell a "known" name
// apart from legacy free text and to select/clear a whole branch in one action.
function namesOf(node) {
  return [node.name, ...node.children.flatMap(namesOf)];
}


// Software Modules In Scope stays free text on ProjectSowDetails.softwareModules (one name per
// line) -- this just constrains what you can pick to the BPA module taxonomy (Module, maintained at
// Project Configuration -> Modules) instead of freehand typing. Entries that don't match a current
// module name -- typed before this existed, or from an older SOW extraction -- are kept, not
// silently dropped, shown as removable chips below the tree.
//
// Rendered as a tree (GET /modules/tree, same endpoint BPATracker's own tree view reads), not a flat
// list, because the taxonomy has real parent/child structure -- a submodule in scope without its
// parent flagged reads as a contradiction. A node's checkbox is "derived" checked when it's named
// directly in the text OR any descendant is, so a submodule checked from stale data (typed before
// this existed) still shows its ancestors as in scope without a migration.
function ModuleScopePicker({ value, onChange }) {
  const [tree, setTree] = useState([]);

  useEffect(() => {
    apiFetch('/modules/tree').then(setTree).catch(() => setTree([]));
  }, []);

  const lines = (value || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const knownNames = new Set(tree.flatMap(namesOf).map((n) => n.toLowerCase()));
  const legacy = lines.filter((l) => !knownNames.has(l.toLowerCase()));

  const isChecked = (node) =>
    lines.some((l) => l.toLowerCase() === node.name.toLowerCase()) || node.children.some(isChecked);

  // Checking a node selects the whole branch under it AND every ancestor above it -- a submodule in
  // scope makes its parent module in scope too, not just visually, so the parent's name is actually
  // added to the saved text. Unchecking clears the branch below but leaves ancestors alone: the
  // reverse isn't implied (a project can still have other submodules of that parent in scope, or the
  // parent may have been flagged deliberately on its own).
  function toggle(node, ancestorNames) {
    const branch = namesOf(node).map((n) => n.toLowerCase());
    if (isChecked(node)) {
      onChange(lines.filter((l) => !branch.includes(l.toLowerCase())).join('\n'));
    } else {
      const present = new Set(lines.map((l) => l.toLowerCase()));
      const toAdd = [...namesOf(node), ...ancestorNames].filter((n) => !present.has(n.toLowerCase()));
      onChange([...lines, ...toAdd].join('\n'));
    }
  }

  function removeLegacy(entry) {
    onChange(lines.filter((l) => l !== entry).join('\n'));
  }

  function TreeNode({ node, depth, ancestorNames }) {
    return (
      <div>
        <label style={{
          display:'flex', alignItems:'center', gap:6, fontSize:13, padding:'3px 0',
          paddingLeft: depth * 18, cursor:'pointer',
        }}>
          <input type="checkbox" checked={isChecked(node)} onChange={() => toggle(node, ancestorNames)} />
          {node.name}
        </label>
        {node.children.map((child) => (
          <TreeNode key={child.id} node={child} depth={depth + 1} ancestorNames={[...ancestorNames, node.name]} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <label className="form-label">Software Modules In Scope</label>
      <div style={{
        border:'1px solid var(--border)', borderRadius:8, padding:10, maxHeight:220,
        overflowY:'auto', background:'var(--surface)',
      }}>
        {tree.map((node) => <TreeNode key={node.id} node={node} depth={0} ancestorNames={[]} />)}
        {tree.length === 0 && (
          <div style={{ fontSize:12, color:'var(--text-mid)' }}>
            No modules configured yet — add some at Project Configuration → Modules.
          </div>
        )}
        {legacy.length > 0 && (
          <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)' }}>
            <div style={{ fontSize:11, color:'var(--text-mid)', marginBottom:4 }}>
              Not in the module list (kept as-is):
            </div>
            {legacy.map((entry) => (
              <span key={entry} style={{
                display:'inline-flex', alignItems:'center', gap:4, fontSize:12,
                background:'var(--light-bg)', border:'1px solid var(--border)', borderRadius:12,
                padding:'2px 8px', marginRight:6, marginBottom:4,
              }}>
                {entry}
                <button
                  type="button"
                  onClick={() => removeLegacy(entry)}
                  style={{ border:'none', background:'none', cursor:'pointer', color:'var(--red)', fontSize:11, padding:0 }}
                >✕</button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PROJECT DETAILS (manual equivalent of what SOW Intake populates) ────────────────────────
function ProjectDetailsPanel({ project, onSaved }) {
  const [data, setData] = useState({
    name: project.name,
    productName: project.productName || '',
    setupStatus: project.setupStatus,
    startDate: project.startDate ? project.startDate.slice(0, 10) : '',
    targetEndDate: project.targetEndDate ? project.targetEndDate.slice(0, 10) : '',
    softwareModules: project.sowDetails?.softwareModules || '',
    services: project.sowDetails?.services || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  function set(key) {
    return (e) => setData((prev) => ({ ...prev, [key]: e.target.value }));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: data.name,
          productName: data.productName || undefined,
          setupStatus: data.setupStatus,
          startDate: data.startDate || undefined,
          targetEndDate: data.targetEndDate || undefined,
        }),
      });
      await apiFetch(`/projects/${project.id}/sow-details`, {
        method: 'PATCH',
        body: JSON.stringify({ softwareModules: data.softwareModules, services: data.services }),
      });
      setSavedAt(new Date());
      await onSaved();
    } catch (err) {
      setError('Could not save project details — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <CollapsibleCard title="Project details">
      <div style={{ fontSize:13, color:'var(--text-mid)', marginBottom:16 }}>
        Everything SOW Intake would otherwise populate — fill it in by hand for a project that
        doesn't start from a SOW, or verify/adjust it for one that did.
      </div>

      {error && (
        <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'rgba(239,65,54,0.08)',
          border:'1px solid rgba(239,65,54,0.3)', fontSize:13, color:'var(--red)' }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Project Name</label>
          <input type="text" className="form-input" value={data.name} onChange={set('name')} />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Customer</label>
          <input type="text" className="form-input" value={project.account?.name || ''} disabled />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Product Name</label>
          <input type="text" className="form-input" value={data.productName} onChange={set('productName')} />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Project Setup Status</label>
          <select className="form-select" value={data.setupStatus} onChange={set('setupStatus')}>
            {PROJECT_SETUP_STATUSES.map((v) => <option key={v}>{v}</option>)}
          </select>
        </div>
      </div>

      {/* RAG status moved to the Project Details tab's Health frame (2026-09-09) -- it now carries a
          narrative and history (StatusReportSnapshot), not just this bare current value. */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Start Date</label>
          <input type="date" className="form-input" value={data.startDate} onChange={set('startDate')} />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Target End Date</label>
          <input type="date" className="form-input" value={data.targetEndDate} onChange={set('targetEndDate')} />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div className="form-group" style={{ marginBottom:0 }}>
          <ModuleScopePicker
            value={data.softwareModules}
            onChange={(v) => setData((prev) => ({ ...prev, softwareModules: v }))}
          />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Services Included</label>
          <textarea className="form-input" rows={4} value={data.services} onChange={set('services')} />
        </div>
      </div>

      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save project details'}
        </button>
        {savedAt && <span style={{ fontSize:12, color:'var(--green)' }}>✓ Saved</span>}
      </div>
    </CollapsibleCard>
  );
}

// ── BILLING MILESTONES ───────────────────────────────────────
// Full add/edit/delete, one grid -- unlike MilestonesSummary (read-only, Details hub), every row here
// is directly editable (spreadsheet-style, no pencil/checkmark toggle): Add/Save live at the top,
// delete (✕) is inline per row. Same MilestoneColumnHeader (drag-reorder + click-to-sort) as
// MilestonesSummary's table, reused as-is. Seq/#/DAS # lead by default (Jeremy, 2026-09-11) -- the
// three fields a PM scans first when reconciling against a SOW or DAS.
// Matches RECURRENCES in packages/shared (web can't import it, ADR 0019).
const RECURRENCES = ['Monthly'];
const MILESTONE_STATUSES = ['Not Started', 'In Progress', 'Delivered', 'Accepted', 'Billed', 'Paid'];

function toMilestoneDraft(m) {
  return {
    id: m.id,
    isNew: false,
    sequence: m.sequence != null ? String(m.sequence) : '',
    sowMilestoneNumber: m.sowMilestoneNumber || '',
    dasNumber: m.dasNumber || '',
    title: m.title || '',
    phaseTitle: m.phase?.title || m.inferredPhase || '',
    workstreamName: m.workstream?.name || '',
    phaseId: m.phaseId || '',
    workstreamId: m.workstreamId || '',
    baselineDate: m.baselineDate ? m.baselineDate.slice(0, 10) : '',
    // Read-only, from _count -- how many times the dates have been edited by hand (ADR 0047).
    moveCount: m._count?.dateChanges ?? 0,
    targetDate: m.targetDate ? m.targetDate.slice(0, 10) : '',
    actualDate: m.actualDate ? m.actualDate.slice(0, 10) : '',
    status: m.status || 'Not Started',
    isGoLive: !!m.isGoLive,
    amount: m.amount != null ? String(m.amount) : '',
    cogs: m.cogs != null ? String(m.cogs) : '',
    recurringIt: !!m.recurringIt,
    billingCategory: m.billingCategory || '',
    valueType: m.valueType || 'Committed',
    recurrence: m.recurrence || '',
    recurrenceEnd: m.recurrenceEnd ? m.recurrenceEnd.slice(0, 10) : '',
    periodsCompleted: String(m.periodsCompleted || 0),
    dateSubmitted: m.dateSubmitted ? m.dateSubmitted.slice(0, 10) : '',
    dateSigned: m.dateSigned ? m.dateSigned.slice(0, 10) : '',
    dateInvoiced: m.dateInvoiced ? m.dateInvoiced.slice(0, 10) : '',
    datePaid: m.datePaid ? m.datePaid.slice(0, 10) : '',
    acceptanceCriteria: m.acceptanceCriteria || '',
  };
}

function blankMilestoneDraft(id) {
  return {
    id, isNew: true, sequence: '', sowMilestoneNumber: '', dasNumber: '', title: '',
    phaseTitle: '', workstreamName: '', phaseId: '', workstreamId: '', baselineDate: '', targetDate: '', actualDate: '',
    status: 'Not Started', isGoLive: false, amount: '', cogs: '', recurringIt: false, billingCategory: '', valueType: 'Committed', recurrence: '', recurrenceEnd: '', periodsCompleted: '0', dateSubmitted: '',
    moveCount: 0,
    dateSigned: '', dateInvoiced: '', datePaid: '', acceptanceCriteria: '',
  };
}

// Blank/NaN numeric fields must serialize to `undefined` (leave alone, not JSON `null`/NaN) -- same
// reasoning the old per-row saveEdit used. Text fields are sent as-is, including '' -- unlike
// numbers/dates, UpdateMilestoneDto's @IsString() fields accept an empty string, so this is how a PM
// actually clears a DAS #/SOW # / Acceptance Criteria that's no longer right, not just leaves it
// alone (`|| undefined` here would silently no-op a cleared field, since undefined omits it from the
// PATCH body entirely). Dates have no such escape hatch -- UpdateMilestoneDto's @IsISO8601() rejects
// '', and there's no clearDate-style flag (unlike clearPhase/clearWorkstream) -- so a blank date field
// still can't be cleared from this grid; it can only be set or left alone, same limit the old
// single-field editor had.
function draftToMilestoneBody(row) {
  const amountNum = Number(row.amount);
  const sequenceNum = Number(row.sequence);
  return {
    title: row.title.trim(),
    // Assigning wins; a blank on an existing row sends the clear flag so a PM can unassign.
    ...(row.phaseId ? { phaseId: row.phaseId } : row.isNew ? {} : { clearPhase: true }),
    ...(row.workstreamId ? { workstreamId: row.workstreamId } : row.isNew ? {} : { clearWorkstream: true }),
    sowMilestoneNumber: row.sowMilestoneNumber,
    dasNumber: row.dasNumber,
    sequence: row.sequence === '' || Number.isNaN(sequenceNum) ? undefined : sequenceNum,
    targetDate: row.targetDate || undefined,
    actualDate: row.actualDate || undefined,
    status: row.status || undefined,
    isGoLive: row.isGoLive,
    amount: row.amount === '' || Number.isNaN(amountNum) ? undefined : amountNum,
    // Blank COGS clears it (null), unlike amount -- a PM removing a wrong cost estimate is a normal edit.
    cogs: row.cogs === '' || Number.isNaN(Number(row.cogs)) ? null : Number(row.cogs),
    recurringIt: row.recurringIt,
    billingCategory: row.billingCategory || undefined,
    valueType: row.valueType || undefined,
    // null turns a series back into a one-off; the API then clears its end and progress.
    recurrence: row.recurrence || null,
    ...(row.recurrence ? { recurrenceEnd: row.recurrenceEnd || undefined, periodsCompleted: Number(row.periodsCompleted) || 0 } : {}),
    dateSubmitted: row.dateSubmitted || undefined,
    dateSigned: row.dateSigned || undefined,
    dateInvoiced: row.dateInvoiced || undefined,
    datePaid: row.datePaid || undefined,
    acceptanceCriteria: row.acceptanceCriteria,
  };
}

// The count on its own is the finding -- "this date has moved four times" -- so it is a button into
// the trail rather than a number you have to go looking for the story behind.
function MilestoneMovesCell({ row, onOpen }) {
  if (row.isNew) return <span style={{ color:'var(--text-light)' }}>—</span>;
  // Nothing recorded date edits before the move log shipped (ADR 0047), so a baseline that no longer
  // matches the target is proof of movement the count can't see. Approximated from those two dates
  // rather than from the log's own earliest row, which would cost a query per grid load — it reads
  // "n+" only where nothing at all is logged, which is every row today and fewer every week.
  const movedBeforeLogging = !row.moveCount && !!row.baselineDate && !!row.targetDate
    && row.baselineDate !== row.targetDate;
  if (!row.moveCount && !movedBeforeLogging) return <span style={{ color:'var(--text-light)' }}>—</span>;
  return (
    <button
      type="button" onClick={onOpen}
      className={row.moveCount > 2 ? 'badge badge-yellow' : 'badge badge-blue'}
      style={{ fontSize:10, border:'none', cursor:'pointer' }}
      title={movedBeforeLogging
        ? 'The target date no longer matches the baseline, so it moved — but the edits predate move '
          + 'logging and were never recorded, so the count is unknown.'
        : "Every edit to this milestone's target or actual date, and every baseline behind it"}
    >{row.moveCount}{movedBeforeLogging ? '+' : '×'}</button>
  );
}

/**
 * One milestone's whole date story: every baseline it has been given, and every hand edit in
 * between. Read together because separately they each tell half of it — a baseline trail misses
 * the slips nobody re-baselined, and the edit log doesn't say what was committed.
 */
function MilestoneHistoryModal({ milestone, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    apiFetch(`/milestones/${milestone.id}/history`)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [milestone.id]);

  const day = (d) => (d ? String(d).slice(0, 10) : '—');
  const when = (d) => (d ? new Date(d).toLocaleString() : '');

  return (
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:60,
        display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
    >
      <div
        className="card" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth:640, width:'100%', maxHeight:'80vh', overflow:'auto' }}
      >
        <div className="card-title" style={{ marginBottom:4 }}>{milestone.title || 'Milestone'}</div>
        <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:14 }}>
          Baseline {milestone.baselineDate || '—'} · Target {milestone.targetDate || '—'} · Actual{' '}
          {milestone.actualDate || '—'}
        </div>

        {error && <div style={{ fontSize:12, color:'var(--red)' }}>{error}</div>}
        {!data && !error && <div style={{ fontSize:12, color:'var(--text-light)' }}>Loading…</div>}

        {data && (
          <>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>
              Date changes ({data.dateChanges.length})
            </div>
            {!data.dateChanges.length ? (
              <div style={{ fontSize:12, color:'var(--text-light)', marginBottom:16 }}>
                No date edits recorded. Changes made before this log existed aren't in it.
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, marginBottom:16 }}>
                <tbody>
                  {data.dateChanges.map((c) => (
                    <tr key={c.id} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>
                        {c.field === 'targetDate' ? 'Target' : 'Actual'}
                      </td>
                      <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>
                        {day(c.previousDate)} → <strong>{day(c.newDate)}</strong>
                      </td>
                      <td style={{ padding:'4px 6px', color:'var(--text-mid)' }}>{c.reason || ''}</td>
                      <td style={{ padding:'4px 6px', color:'var(--text-light)', whiteSpace:'nowrap' }}>
                        {when(c.changedAt)} · {c.changedBy?.name || c.changedByName || 'unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ fontSize:12, fontWeight:600, marginBottom:6 }}>
              Baselines ({data.baselines.length})
            </div>
            {!data.baselines.length ? (
              <div style={{ fontSize:12, color:'var(--text-light)' }}>Never baselined.</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <tbody>
                  {data.baselines.map((b) => (
                    <tr key={b.id} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>Rev {b.revision}</td>
                      <td style={{ padding:'4px 6px', whiteSpace:'nowrap' }}>
                        {b.previousDate ? `${day(b.previousDate)} → ` : ''}
                        <strong>{day(b.baselineDate)}</strong>
                      </td>
                      <td style={{ padding:'4px 6px', color:'var(--text-mid)' }}>{b.reason || ''}</td>
                      <td style={{ padding:'4px 6px', color:'var(--text-light)', whiteSpace:'nowrap' }}>
                        {when(b.setAt)} · {b.setBy?.name || 'unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        <div style={{ marginTop:16 }}>
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function setupMilestoneValue(row, key) {
  if (key === 'moveCount') return Number(row.moveCount || 0);
  if (key === 'amount') return row.amount === '' ? -1 : Number(row.amount);
  if (key === 'cogs') return row.cogs === '' ? -1 : Number(row.cogs);
  if (key === 'net') return row.amount === '' ? -1 : Number(row.amount) - Number(row.cogs || 0);
  if (key === 'sequence') return row.sequence === '' ? -1 : Number(row.sequence);
  return lower(String(row[key] ?? ''));
}

// Phase/Workstream lead, Setup-only (Jeremy, 2026-09-11) -- this is the one grid that shows them,
// since reassigning either needs the id-bearing pickers, not just a label. Go-Live isn't a column
// (dropped from all three milestone grids) -- folded into the title editor below instead.
const SETUP_MILESTONE_COLUMNS = [
  { key: 'phaseTitle', label: 'Phase' },
  { key: 'workstreamName', label: 'Workstream' },
  { key: 'sequence', label: 'Seq' },
  { key: 'sowMilestoneNumber', label: '#' },
  { key: 'dasNumber', label: 'DAS #' },
  { key: 'title', label: 'Milestone' },
  { key: 'amount', label: 'Gross' },
  { key: 'cogs', label: 'COGS' },
  { key: 'net', label: 'Net' },
  { key: 'billingCategory', label: 'Category' },
  { key: 'valueType', label: 'Value' },
  { key: 'recurrence', label: 'Repeats' },
  { key: 'status', label: 'Status' },
  { key: 'baselineDate', label: 'Baseline' },
  { key: 'targetDate', label: 'Target' },
  { key: 'actualDate', label: 'Actual' },
  { key: 'moveCount', label: 'Moves' },
  { key: 'dateSubmitted', label: 'Submitted' },
  { key: 'dateSigned', label: 'Signed' },
  { key: 'dateInvoiced', label: 'Invoiced' },
  { key: 'datePaid', label: 'Paid' },
  { key: 'acceptanceCriteria', label: 'Acceptance Criteria' },
];
const SETUP_MILESTONE_COLUMN_KEYS = SETUP_MILESTONE_COLUMNS.map((c) => c.key);
const SETUP_MILESTONE_COLUMNS_BY_KEY = Object.fromEntries(SETUP_MILESTONE_COLUMNS.map((c) => [c.key, c]));

// Third arg carries the project's phases/workstreams for the two id-bearing pickers.
const MILESTONE_EDIT_FIELDS = {
  phaseTitle: (row, set, { phases }) => (
    <select className="form-select" style={{ fontSize:12, width:130 }} value={row.phaseId}
      onChange={(e) => set('phaseId', e.target.value)}>
      <option value="">{row.phaseId ? '' : (row.phaseTitle ? `— ${row.phaseTitle} (unmatched)` : '— unassigned —')}</option>
      {phases.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
    </select>
  ),
  workstreamName: (row, set, { workstreams }) => (
    <select className="form-select" style={{ fontSize:12, width:130 }} value={row.workstreamId}
      onChange={(e) => set('workstreamId', e.target.value)}>
      <option value="">— none —</option>
      {workstreams.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
    </select>
  ),
  sequence: (row, set) => (
    <input type="number" className="form-input" style={{ fontSize:12, width:56 }} value={row.sequence}
      onChange={(e) => set('sequence', e.target.value)} />
  ),
  sowMilestoneNumber: (row, set) => (
    <input className="form-input" style={{ fontSize:12, width:56 }} value={row.sowMilestoneNumber}
      onChange={(e) => set('sowMilestoneNumber', e.target.value)} />
  ),
  dasNumber: (row, set) => (
    <input className="form-input" style={{ fontSize:12, width:80 }} value={row.dasNumber}
      onChange={(e) => set('dasNumber', e.target.value)} />
  ),
  title: (row, set) => (
    <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:180 }}>
      <input className="form-input" style={{ fontSize:12 }} value={row.title}
        onChange={(e) => set('title', e.target.value)} />
      <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--text-mid)' }}>
        <input type="checkbox" checked={row.isGoLive} onChange={(e) => set('isGoLive', e.target.checked)} />
        Go-Live
      </label>
      <label style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--text-mid)' }}
        title="Billing for the monthly Recurring IT fee. Revenue comes from the project's Recurring IT amount, so the Revenue Tracker doesn't count this sign-off again.">
        <input type="checkbox" checked={row.recurringIt} onChange={(e) => set('recurringIt', e.target.checked)} />
        Recurring IT
      </label>
    </div>
  ),
  targetDate: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.targetDate}
      onChange={(e) => set('targetDate', e.target.value)} />
  ),
  actualDate: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.actualDate}
      onChange={(e) => set('actualDate', e.target.value)} />
  ),
  status: (row, set) => (
    <select className="form-select" style={{ fontSize:12 }} value={row.status} onChange={(e) => set('status', e.target.value)}>
      {MILESTONE_STATUSES.map((s) => <option key={s}>{s}</option>)}
    </select>
  ),
  amount: (row, set) => (
    <input type="number" className="form-input" style={{ fontSize:12, width:90 }} value={row.amount}
      onChange={(e) => set('amount', e.target.value)} />
  ),
  cogs: (row, set) => (
    <input type="number" className="form-input" style={{ fontSize:12, width:90 }} value={row.cogs}
      onChange={(e) => set('cogs', e.target.value)} title="Cost of goods sold recognized with this sign-off" />
  ),
  // Read-only: gross minus COGS.
  net: (row) => (row.amount === '' ? '—' : (
    <span style={{ fontSize:12, whiteSpace:'nowrap' }}>${(Number(row.amount) - Number(row.cogs || 0)).toLocaleString()}</span>
  )),
  billingCategory: (row, set) => (
    <select className="form-select" style={{ fontSize:12, width:95 }} value={row.billingCategory}
      onChange={(e) => set('billingCategory', e.target.value)}>
      <option value="">—</option>
      {BILLING_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
    </select>
  ),
  valueType: (row, set) => (
    <select className="form-select" style={{ fontSize:12, width:110 }} value={row.valueType}
      onChange={(e) => set('valueType', e.target.value)}
      title="Draw-down and Optional only bill if the customer uses or authorizes the work">
      {VALUE_TYPES.map((v) => <option key={v}>{v}</option>)}
    </select>
  ),
  recurrence: (row, set) => (
    <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:130 }}>
      <select className="form-select" style={{ fontSize:12 }} value={row.recurrence}
        onChange={(e) => set('recurrence', e.target.value)}
        title="Repeats on the target date's day each month, one billing period per month. Amount is the total across all periods.">
        <option value="">One-time</option>
        {RECURRENCES.map((r) => <option key={r}>{r}</option>)}
      </select>
      {row.recurrence && (
        <>
          <label style={{ fontSize:10, color:'var(--text-mid)' }}>Last period
            <input type="date" className="form-input" style={{ fontSize:12 }} value={row.recurrenceEnd}
              onChange={(e) => set('recurrenceEnd', e.target.value)} />
          </label>
          <label style={{ fontSize:10, color:'var(--text-mid)' }}>Periods done
            <input type="number" min="0" className="form-input" style={{ fontSize:12, width:70 }} value={row.periodsCompleted}
              onChange={(e) => set('periodsCompleted', e.target.value)} />
          </label>
        </>
      )}
    </div>
  ),
  dateSubmitted: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.dateSubmitted}
      onChange={(e) => set('dateSubmitted', e.target.value)} />
  ),
  dateSigned: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.dateSigned}
      onChange={(e) => set('dateSigned', e.target.value)} />
  ),
  dateInvoiced: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.dateInvoiced}
      onChange={(e) => set('dateInvoiced', e.target.value)} />
  ),
  datePaid: (row, set) => (
    <input type="date" className="form-input" style={{ fontSize:12 }} value={row.datePaid}
      onChange={(e) => set('datePaid', e.target.value)} />
  ),
  acceptanceCriteria: (row, set) => (
    <input className="form-input" style={{ fontSize:12, minWidth:160 }} value={row.acceptanceCriteria}
      onChange={(e) => set('acceptanceCriteria', e.target.value)} />
  ),
};

// Recurring IT (e.g. Cuyahoga's monthly PMO fee): a flat $/month recognized regardless of hours --
// a Ratable billing rate under the hood. A change is a new amount from a month on; 0 ends it.
function RecurringItSection({ project }) {
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try { setRows((await apiFetch(`/projects/${project.id}/billing-rates`)).filter((r) => r.kind === 'Ratable')); }
    catch (err) { setError(err.message); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    const n = Number(amount);
    if (amount === '' || !(n >= 0)) { setError('Enter a monthly amount (0 ends it)'); return; }
    setSaving(true); setError('');
    try {
      await apiFetch(`/projects/${project.id}/billing-rate`, { method: 'POST', body: JSON.stringify({ rate: n, kind: 'Ratable', effectiveDate: `${from}-01` }) });
      setAmount('');
      await load();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  const month = (d) => String(d).slice(0, 7);
  const thisMonth = new Date().toISOString().slice(0, 7);
  const current = rows.find((r) => month(r.effectiveDate) <= thisMonth); // rows arrive newest-first
  const fmt = (n) => `$${Number(n).toLocaleString()}`;
  return (
    <CollapsibleCard id="recurring-it-frame" title="Recurring IT revenue (monthly)">
      <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:8 }}>
        A flat amount recognized every month regardless of hours logged (e.g. PMO). Reported on the Revenue Tracker's Recurring IT line, alongside any milestone revenue.
      </div>
      <div style={{ fontSize:13, marginBottom:10 }}>
        {current && Number(current.rate) > 0
          ? <>Now <b>{fmt(current.rate)}/month</b> since {month(current.effectiveDate)}</>
          : <span style={{ color:'var(--text-mid)' }}>No recurring IT revenue.</span>}
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <input type="number" min="0" step="0.01" className="form-input" style={{ width:120 }} placeholder="$/month" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <span style={{ fontSize:12, color:'var(--text-mid)' }}>from</span>
        <input type="month" className="form-input" style={{ width:150 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        <button className="btn btn-primary" style={{ padding:'4px 10px', fontSize:12 }} onClick={save} disabled={saving}>{saving ? '…' : current ? 'Change amount' : 'Set amount'}</button>
        {error && <span style={{ fontSize:12, color:'var(--red)' }}>⚠ {error}</span>}
      </div>
      {rows.length > 0 && (
        <table className="data-table" style={{ width:'100%', fontSize:12, marginTop:10 }}>
          <thead><tr><th style={{ textAlign:'left' }}>From</th><th style={{ textAlign:'left' }}>Monthly</th><th style={{ textAlign:'left' }}>Entered</th></tr></thead>
          <tbody>{rows.map((r) => <tr key={r.id}><td>{month(r.effectiveDate)}</td><td>{Number(r.rate) > 0 ? fmt(r.rate) : 'Ended'}</td><td style={{ color:'var(--text-mid)' }}>{String(r.createdAt).slice(0, 10)}</td></tr>)}</tbody>
        </table>
      )}
    </CollapsibleCard>
  );
}

function MilestonesSection({ project }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [rowBusyId, setRowBusyId] = useState(null);
  const [sort, toggleSort] = useGridSort('sequence');
  const [columnOrder, setColumnOrder] = useState(SETUP_MILESTONE_COLUMN_KEYS);
  const [draggedKey, setDraggedKey] = useState(null);
  const [baselining, setBaselining] = useState(false);
  const [baselineMsg, setBaselineMsg] = useState('');
  // The milestone whose date history is open, if any.
  const [historyFor, setHistoryFor] = useState(null);
  const [lists, setLists] = useState({ phases: [], workstreams: [] });
  // Sort reads this snapshot, not `rows` directly -- sortRows' own contract (Configuration.js) is
  // that it sorts saved values, not an in-flight draft, so a row being typed into doesn't jump
  // position out from under the cursor on every keystroke. Refreshed on load and after a save;
  // a brand-new unsaved row has no snapshot entry, so it sorts by its own live values (nothing
  // "saved" to fall back to).
  const sortSnapshot = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const milestones = await apiFetch(`/milestones?projectId=${project.id}`);
      const drafts = milestones.map(toMilestoneDraft);
      sortSnapshot.current = Object.fromEntries(drafts.map((r) => [r.id, r]));
      setRows(drafts);
      setDirtyIds(new Set());
    } catch (err) {
      setError('Could not load milestones — ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  // Phases/workstreams for the pickers. Failure leaves them empty (pickers show only the current
  // value) rather than blocking the grid.
  useEffect(() => {
    let cancelled = false;
    apiFetch(`/gantt/${project.id}`)
      .then((s) => { if (!cancelled) setLists({ phases: s.phases || [], workstreams: s.workstreams || [] }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [project.id]);

  function updateField(id, field, value) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  function addRow() {
    const id = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setRows((prev) => [...prev, blankMilestoneDraft(id)]);
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Copies the planning fields into a new unsaved row; the identifiers and history (#, DAS #, seq,
  // baseline, actual, billing dates) are left blank because they belong to the original.
  function duplicateRow(id) {
    const src = rows.find((r) => r.id === id);
    if (!src) return;
    const newId = `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setRows((prev) => [...prev, {
      ...blankMilestoneDraft(newId),
      title: `${src.title} (copy)`,
      phaseTitle: src.phaseTitle, workstreamName: src.workstreamName,
      phaseId: src.phaseId, workstreamId: src.workstreamId,
      targetDate: src.targetDate, amount: src.amount, cogs: src.cogs, recurringIt: src.recurringIt, billingCategory: src.billingCategory,
      valueType: src.valueType, acceptanceCriteria: src.acceptanceCriteria,
    }]);
    setDirtyIds((prev) => new Set(prev).add(newId));
  }

  async function removeRow(id) {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (row.isNew) {
      setRows((prev) => prev.filter((r) => r.id !== id));
      setDirtyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
      return;
    }
    if (!window.confirm("Delete this milestone? This can't be undone.")) return;
    setRowBusyId(id);
    setError('');
    try {
      await apiFetch(`/milestones/${id}`, { method: 'DELETE' });
      setRows((prev) => prev.filter((r) => r.id !== id));
      setDirtyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } catch (err) {
      setError('Could not delete that milestone — ' + err.message);
    } finally {
      setRowBusyId(null);
    }
  }

  // Saves only the dirty rows with a name, in place -- no full reload afterward. A reload would
  // wholesale-replace `rows` from the server, silently discarding two things: a row skipped here for
  // having no title yet (still dirty, still unsaved, but no longer visible as such), and any edit
  // made to a *different* row while this save's requests were in flight. Applying each response
  // directly to its own row (allSettled, not all -- one bad PATCH shouldn't block the rows that
  // succeeded) avoids both, and skips the extra GET round-trip the old `load()` call made too.
  async function handleSaveAll() {
    const dirty = rows.filter((r) => dirtyIds.has(r.id));
    const toSave = dirty.filter((r) => r.title.trim());
    const skipped = dirty.length - toSave.length;
    if (!toSave.length) {
      if (skipped) setError(`${skipped} milestone${skipped > 1 ? 's' : ''} need a name before they can be saved.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const results = await Promise.allSettled(toSave.map((r) => {
        const body = draftToMilestoneBody(r);
        return r.isNew
          ? apiFetch('/milestones', { method: 'POST', body: JSON.stringify({ projectId: project.id, ...body }) })
          : apiFetch(`/milestones/${r.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      }));

      const failedTitles = [];
      setRows((prev) => {
        let next = prev;
        results.forEach((result, i) => {
          const draftRow = toSave[i];
          if (result.status === 'fulfilled') {
            const fresh = toMilestoneDraft(result.value);
            sortSnapshot.current = { ...sortSnapshot.current, [fresh.id]: fresh };
            next = next.map((r) => (r.id === draftRow.id ? fresh : r));
          } else {
            failedTitles.push(draftRow.title || '(untitled)');
          }
        });
        return next;
      });
      setDirtyIds((prev) => {
        const next = new Set(prev);
        results.forEach((result, i) => { if (result.status === 'fulfilled') next.delete(toSave[i].id); });
        return next;
      });

      if (failedTitles.length) {
        setError(`Could not save: ${failedTitles.join(', ')}`);
      } else if (skipped) {
        setError(`${skipped} milestone${skipped > 1 ? 's' : ''} need a name before they can be saved.`);
      } else {
        setSavedAt(new Date());
      }
    } catch (err) {
      setError('Could not save milestones — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  function reorderColumns(targetKey) {
    if (!draggedKey || draggedKey === targetKey) { setDraggedKey(null); return; }
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== draggedKey);
      next.splice(next.indexOf(targetKey), 0, draggedKey);
      return next;
    });
    setDraggedKey(null);
  }

  // Stamps today's target dates as the baseline for the whole project. Re-baselining (after a
  // change order) needs a reason and is confirmed separately, because it displaces the original
  // commitment the Slip column is measured against.
  async function handleBaseline() {
    const alreadyBaselined = rows.filter((r) => r.baselineDate).length;
    const rebaseline = alreadyBaselined > 0 && window.confirm(
      `${alreadyBaselined} of ${rows.length} milestones already have a baseline.\n\n`
      + 'OK = re-baseline everything to the current target dates (for a change order).\n'
      + 'Cancel = only baseline the ones that have none yet.',
    );
    let reason = null;
    if (rebaseline) {
      reason = window.prompt('Why is the schedule being re-baselined? (e.g. CO-004 scope change)');
      if (!reason || !reason.trim()) return;
    }
    // Blank is fine and normal -- the server names it "Baseline N". Only a baseline worth finding
    // by name later earns one typed by hand.
    const name = window.prompt(
      'Name this baseline (leave blank for the next number):',
      rebaseline && reason ? reason.trim() : '',
    );
    if (name === null) return;
    setBaselining(true);
    setBaselineMsg('');
    try {
      const result = await apiFetch('/milestones/baseline', {
        method: 'POST',
        body: JSON.stringify({
          projectId: project.id, rebaseline, reason: reason || undefined, name: name.trim() || undefined,
        }),
      });
      setBaselineMsg(
        `✓ ${result.baselined} baselined${result.skipped ? `, ${result.skipped} unchanged` : ''}`
        + (result.snapshot ? ` · saved as "${result.snapshot.name}"` : ''),
      );
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBaselining(false);
      setTimeout(() => setBaselineMsg(''), 6000);
    }
  }

  const columns = columnOrder.map((key) => SETUP_MILESTONE_COLUMNS_BY_KEY[key]);
  const sorted = sortRows(rows, sort, (row, key) => setupMilestoneValue(sortSnapshot.current[row.id] || row, key));

  return (
    <CollapsibleCard
      id="milestones-setup-frame"
      title="Billing Milestones"
      actions={
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {savedAt && <span style={{ color:'var(--green)' }}>✓ Saved</span>}
          {baselineMsg && <span style={{ color:'var(--text-mid)' }}>{baselineMsg}</span>}
          <Link
            to={projectPath('/schedule', project.id, { panel: 'snapshots' })}
            className="btn btn-outline" style={{ fontSize:11, padding:'3px 10px' }}
            title="Every baseline this project has set, side by side, with what has moved since each one"
          >View baselines</Link>
          <button
            className="btn btn-outline" style={{ fontSize:11, padding:'3px 10px' }}
            onClick={handleBaseline} disabled={baselining || dirtyIds.size > 0}
            title={dirtyIds.size > 0
              ? 'Save your changes first — baselining stamps the saved target dates'
              : 'Stamp the current target dates as the baseline. Milestones already baselined are left alone; the Slip column measures against this.'}
          >{baselining ? 'Baselining…' : 'Baseline schedule'}</button>
          <button className="btn btn-outline" style={{ fontSize:11, padding:'3px 10px' }} onClick={addRow}>+ Add</button>
          <button
            className="btn btn-primary" style={{ fontSize:11, padding:'3px 10px' }}
            onClick={handleSaveAll} disabled={saving || dirtyIds.size === 0}
          >{saving ? 'Saving…' : 'Save'}</button>
        </div>
      }
    >
      {error && <div style={{ fontSize:12, color:'var(--red)', marginBottom:10 }}>⚠ {error}</div>}
      {loading ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize:12, color:'var(--text-mid)' }}>No milestones yet — click + Add to create one.</div>
      ) : (
        <div style={{ overflow:'auto', maxHeight:480, border:'1px solid var(--border)', borderRadius:8 }}>
          <table className="sheet" style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
            <thead>
              <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                {columns.map((col) => (
                  <MilestoneColumnHeader
                    key={col.key} col={col} sort={sort} onSort={toggleSort}
                    draggedKey={draggedKey} onDragStart={setDraggedKey} onDrop={reorderColumns}
                  />
                ))}
                <th style={{ padding:'6px 10px', width:60, position:'sticky', top:0, background:'var(--light-bg)' }} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const busy = rowBusyId === row.id;
                return (
                  <tr
                    key={row.id}
                    className={dirtyIds.has(row.id) ? 'is-dirty' : undefined}
                    style={{ borderTop:'1px solid var(--border)' }}
                  >
                    {columns.map((col) => (
                      <td key={col.key} style={{ padding:'4px' }}>
                        {col.key === 'moveCount' ? (
                          <MilestoneMovesCell row={row} onOpen={() => setHistoryFor(row)} />
                        ) : MILESTONE_EDIT_FIELDS[col.key]
                          ? MILESTONE_EDIT_FIELDS[col.key](row, (field, value) => updateField(row.id, field, value), lists)
                          : (row[col.key] || '—')}
                      </td>
                    ))}
                    <td style={{ padding:'4px', whiteSpace:'nowrap' }}>
                      <button
                        onClick={() => duplicateRow(row.id)}
                        disabled={busy || row.isNew}
                        title="Duplicate — adds an unsaved copy to edit, then Save"
                        style={{ fontSize:13, color:'var(--text-mid)', background:'none', border:'none', cursor:'pointer', padding:'2px 4px' }}
                      >⧉</button>
                      <button
                        onClick={() => removeRow(row.id)}
                        disabled={busy}
                        style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer', padding:'2px 4px' }}
                      >{busy ? '…' : '✕'}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {historyFor && (
        <MilestoneHistoryModal milestone={historyFor} onClose={() => setHistoryFor(null)} />
      )}
    </CollapsibleCard>
  );
}

// ── FOLDER STRUCTURE (ported from ProjectSetup.js's StepFolderStructure) ─────────────────────
// Every module folder starts with this same trio -- reused by both the initial tree build and the
// reconciliation effect below, so a newly-added module gets the identical template a project's
// modules got at mount.
function moduleFolderTemplate(name) {
  return {
    name,
    children: [
      { name: 'Business Process Analysis (BPA)', children: [] },
      { name: 'Process Guides', children: [] },
      { name: 'Trainings', children: [] },
    ],
  };
}

function FolderStructureSection({ project }) {
  const modules = project.sowDetails?.softwareModules
    ? project.sowDetails.softwareModules.split('\n').map((m) => m.trim()).filter(Boolean)
    : [];

  const buildInitialTree = () => {
    const moduleLeads = modules.map(moduleFolderTemplate);

    const conversionFolders = Array.from({ length: 6 }, (_, i) => ({ name: `Conversion ${i}`, children: [] }));

    return [
      { name: 'Aumentum Modules', children: moduleLeads },
      { name: 'Conversion', children: conversionFolders },
      { name: 'Customer Provided Files', children: [] },
      { name: 'E2E Testing', children: [] },
      { name: 'Product Deliverables', children: [] },
      {
        name: 'Project Management', children: [
          { name: 'Change Requests', children: [] },
          { name: 'Contract', children: [] },
          { name: 'LOAs', children: [] },
          { name: 'Meeting Notes', children: [] },
          { name: 'Project Schedule', children: [] },
          { name: 'Recordings', children: [] },
          { name: 'Training Matrices', children: [] },
          { name: 'Transition from Implementation to Support', children: [] },
        ],
      },
    ];
  };

  const [tree, setTree] = useState(buildInitialTree);
  const [editingPath, setEditingPath] = useState(null);

  // Keeps the "Aumentum Modules" folder in sync with Software Modules In Scope after the first
  // render -- `buildInitialTree` above only ever runs once (React's lazy useState initializer), so
  // without this, adding or removing a module in Project Details left the preview showing whatever
  // was in scope when the page first loaded. Scoped to a real change in the underlying SOW field
  // (not `modules`, a fresh array every render) so it doesn't refire on unrelated re-renders.
  //
  // Only "Aumentum Modules" is touched -- Conversion/Customer Provided Files/etc. are hand-maintained
  // and never module-driven, so they're left exactly as the PM last edited them. A module folder
  // that's still in scope keeps whatever the PM has already renamed/added/removed under it; only
  // add/remove is reconciled, matched case-insensitively so a module renamed only in casing doesn't
  // get treated as removed-then-re-added and lose its edits.
  useEffect(() => {
    const current = project.sowDetails?.softwareModules
      ? project.sowDetails.softwareModules.split('\n').map((m) => m.trim()).filter(Boolean)
      : [];
    setTree((prev) => prev.map((topNode) => {
      if (topNode.name !== 'Aumentum Modules') return topNode;
      const existingByName = new Map(topNode.children.map((c) => [c.name.toLowerCase(), c]));
      return {
        ...topNode,
        children: current.map((name) => existingByName.get(name.toLowerCase()) ?? moduleFolderTemplate(name)),
      };
    }));
  }, [project.sowDetails?.softwareModules]);

  const customerFolder = folderParts(project.name).customer;

  // Already-created status shows in the Initialize Project frame below — no need to say it twice.
  // Module changes stop reaching real SharePoint folders once they exist -- there's no provisioning
  // route to add/remove a single folder from an already-created structure (only "create everything"
  // exists today), so this reconciliation only ever affects the pre-creation preview above.
  if (project.internalFoldersCreated) return null;

  function handleRename(path, newName) {
    setTree((prev) => {
      const newTree = JSON.parse(JSON.stringify(prev));
      let node = { children: newTree };
      for (let i = 0; i < path.length - 1; i++) node = node.children[path[i]];
      node.children[path[path.length - 1]].name = newName;
      return newTree;
    });
    setEditingPath(null);
  }

  function handleDelete(path) {
    setTree((prev) => {
      const newTree = JSON.parse(JSON.stringify(prev));
      let node = { children: newTree };
      for (let i = 0; i < path.length - 1; i++) node = node.children[path[i]];
      node.children.splice(path[path.length - 1], 1);
      return newTree;
    });
  }

  function handleAdd(path) {
    const name = prompt('New folder name:');
    if (!name) return;
    setTree((prev) => {
      const newTree = JSON.parse(JSON.stringify(prev));
      let node = { children: newTree };
      for (const idx of path) node = node.children[idx];
      node.children.push({ name, children: [] });
      return newTree;
    });
  }

  function FolderNode({ node, path, depth }) {
    const isEditing = editingPath && editingPath.join(',') === path.join(',');
    const indent = depth * 20;

    return (
      <div>
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'5px 8px', borderRadius:6,
          marginLeft: indent,
          background: depth === 0 ? 'rgba(4,64,96,0.04)' : 'transparent',
          borderLeft: depth === 0 ? '3px solid var(--primary-light)' : '1px solid var(--border)',
          marginBottom:3,
        }}>
          <span style={{ fontSize:13 }}>📁</span>
          {isEditing ? (
            <input
              autoFocus
              type="text"
              defaultValue={node.name}
              style={{ flex:1, fontSize:13, padding:'2px 6px', border:'1px solid var(--primary-light)', borderRadius:4 }}
              onBlur={(e) => handleRename(path, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(path, e.target.value); if (e.key === 'Escape') setEditingPath(null); }}
            />
          ) : (
            <span style={{ flex:1, fontSize:13, color:'var(--text-dark)' }}>{node.name}</span>
          )}
          <button
            onClick={() => setEditingPath(path)}
            style={{ fontSize:11, color:'var(--primary-light)', background:'none', border:'none', cursor:'pointer', padding:'2px 6px' }}
          >✏️</button>
          <button
            onClick={() => handleAdd(path)}
            style={{ fontSize:11, color:'var(--green)', background:'none', border:'none', cursor:'pointer', padding:'2px 6px' }}
          >＋</button>
          <button
            onClick={() => handleDelete(path)}
            style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer', padding:'2px 6px' }}
          >✕</button>
        </div>
        {node.children.map((child, i) => (
          <FolderNode key={i} node={child} path={[...path, i]} depth={depth + 1} />
        ))}
      </div>
    );
  }

  return (
    <CollapsibleCard title="Folder structure">
      <div style={{
        padding:'10px 14px', borderRadius:8, marginBottom:16,
        background:'var(--light-bg)', border:'1px solid var(--border)',
        fontSize:12, color:'var(--text-mid)',
      }}>
        📍 Will be created under: <strong>Internal Project Folders / {customerFolder}</strong>
      </div>

      <div style={{ marginBottom:8, fontSize:12, color:'var(--text-mid)' }}>
        Click ✏️ to rename · ＋ to add subfolder · ✕ to remove
      </div>

      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, padding:16 }}>
        {tree.map((node, i) => (
          <FolderNode key={i} node={node} path={[i]} depth={0} />
        ))}
      </div>

      <button
        onClick={() => handleAdd([])}
        style={{
          marginTop:12, fontSize:12, color:'var(--primary-light)',
          background:'none', border:'1px dashed var(--primary-light)',
          borderRadius:6, padding:'6px 14px', cursor:'pointer', width:'100%',
        }}
      >
        ＋ Add top-level folder
      </button>
    </CollapsibleCard>
  );
}

// ── INITIALIZE (ported from ProjectSetup.js's StepInitialize) ───────────────────────────────
function InitializeSection({ project, onChanged }) {
  const modules = project.sowDetails?.softwareModules
    ? project.sowDetails.softwareModules.split('\n').map((m) => m.trim()).filter(Boolean)
    : [];

  const [status, setStatus] = useState({
    folders: project.internalFoldersCreated ? 'success' : 'pending',
    customerSite: project.customerSiteCreated ? 'success' : 'pending',
  });
  const [errorDetail, setErrorDetail] = useState({});
  const pollRef = React.useRef(null);
  const pollCountRef = React.useRef(0);

  React.useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  function stopPolling() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  function startPollingCustomerSite() {
    stopPolling();
    pollCountRef.current = 0;
    pollRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 180) {
        stopPolling();
        return;
      }
      try {
        const fresh = await apiFetch(`/projects/${project.id}`);
        if (fresh.customerSiteCreated) {
          stopPolling();
          setStatus((s) => ({ ...s, customerSite: 'success' }));
          onChanged();
        }
      } catch {
        // Transient poll failure -- keep trying rather than giving up on one missed check.
      }
    }, 10000);
  }

  const STATUS_CONFIG = {
    pending: { label: 'Not started', color: 'var(--text-mid)', icon: '○' },
    running: { label: 'Creating…',   color: 'var(--primary-light)', icon: '◌' },
    success: { label: 'Complete',    color: 'var(--green)', icon: '●' },
    error:   { label: 'Failed',      color: 'var(--red)', icon: '✕' },
  };

  async function handleCreateFolders() {
    setStatus((s) => ({ ...s, folders: 'running' }));
    setErrorDetail((d) => ({ ...d, folders: '' }));
    try {
      const customerFolder = folderParts(project.name).customer;
      const projectFolder = folderParts(project.name).project;
      const body = { customerFolder, projectFolder, modules, iterations: 5, projectId: project.id };
      if (PROVISIONING_SOURCE === 'new') {
        await apiFetch('/provisioning/folders', { method: 'POST', body: JSON.stringify(body) });
      } else {
        const response = await fetch(CREATE_FOLDERS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Status ' + response.status);
      }
      setStatus((s) => ({ ...s, folders: 'success' }));
      onChanged();
    } catch (err) {
      setStatus((s) => ({ ...s, folders: 'error' }));
      setErrorDetail((d) => ({ ...d, folders: err.message }));
    }
  }

  async function handleCreateCustomerSite() {
    setStatus((s) => ({ ...s, customerSite: 'running' }));
    setErrorDetail((d) => ({ ...d, customerSite: '' }));
    const customerFolder = folderParts(project.name).customer;
    const body = { projectId: project.id, customerFolder };
    try {
      if (PROVISIONING_SOURCE === 'new') {
        await apiFetch('/provisioning/customer-site', { method: 'POST', body: JSON.stringify(body) });
      } else {
        const response = await fetch(CREATE_CUSTOMER_SUBSITE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error('Status ' + response.status);
      }
    } catch (err) {
      setErrorDetail((d) => ({ ...d, customerSite: `Still checking for completion (kickoff response: ${err.message})` }));
    }
    startPollingCustomerSite();
  }

  const customerFolder = folderParts(project.name).customer;

  const INIT_STEPS = [
    {
      key: 'folders',
      label: 'Create SharePoint folder structure',
      desc: project.internalFoldersUrl || 'Internal Project Folders / ' + customerFolder,
      action: handleCreateFolders,
      actionLabel: 'Create Folders',
      link: status.folders === 'success' && project.internalFoldersUrl ? project.internalFoldersUrl : null,
    },
    {
      key: 'customerSite',
      label: 'Create customer share site',
      desc: status.customerSite === 'running'
        ? 'Provisioning can take several minutes — checking automatically every 10s'
        : `${customerFolder}-External · 5 standard document folders`,
      action: handleCreateCustomerSite,
      actionLabel: 'Create Customer Site',
      link: null,
    },
  ];

  return (
    <CollapsibleCard title="Initialize project">
      <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 20 }}>
        Each step can be completed independently. Status is saved to SharePoint so you can return to this screen at any time.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {INIT_STEPS.map((step) => {
          const s = STATUS_CONFIG[status[step.key]];
          const isRunning = status[step.key] === 'running';
          const isDone = status[step.key] === 'success';
          return (
            <div key={step.key} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 18px', borderRadius: 8,
              background: isDone ? 'rgba(0,124,100,0.06)' : 'var(--light-bg)',
              border: `1px solid ${isDone ? 'rgba(0,124,100,0.3)' : 'var(--border)'}`,
              transition: 'all 0.2s',
            }}>
              <span style={{ fontSize: 20, color: s.color, width: 24, textAlign: 'center', flexShrink: 0 }}>
                {s.icon}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>
                  {step.label}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>{step.desc}</div>
                {errorDetail[step.key] && (
                  <div style={{
                    fontSize: 12, marginTop: 4,
                    color: status[step.key] === 'error' ? 'var(--red)' : 'var(--text-mid)',
                  }}>
                    {status[step.key] === 'error' ? '⚠ ' : '⏳ '}{errorDetail[step.key]}
                  </div>
                )}
                {step.link && (
                  <a
                    href={step.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, color: 'var(--primary-light)', marginTop: 4, display: 'inline-block' }}
                  >
                    Open site →
                  </a>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.label}</span>
                {!isDone && (
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: 12, padding: '6px 14px', opacity: isRunning ? 0.5 : 1 }}
                    disabled={isRunning}
                    onClick={step.action}
                  >
                    {isRunning ? 'Working…' : step.actionLabel}
                  </button>
                )}
                {isDone && (
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: 11, padding: '4px 10px', color: 'var(--text-mid)' }}
                    onClick={step.action}
                  >
                    Redo
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </CollapsibleCard>
  );
}

const STATUS_COLORS = {
  'Not Started': { bg:'var(--light-bg)', border:'var(--border)', text:'var(--text-mid)' },
  'In Progress': { bg:'rgba(4,64,96,0.06)', border:'rgba(4,64,96,0.3)', text:'var(--heading)' },
  'Complete':    { bg:'rgba(0,124,100,0.06)', border:'rgba(0,124,100,0.3)', text:'var(--green)' },
  'N/A':         { bg:'var(--light-bg)', border:'var(--border)', text:'var(--text-mid)' },
};

function ChecklistItemRow({ item, project, onUpdate, onRemove }) {
  const colors = STATUS_COLORS[item.status] || STATUS_COLORS['Not Started'];
  const [notes, setNotes] = useState(item.notes || '');
  const [showNotes, setShowNotes] = useState(!!item.notes);
  const [docUrl, setDocUrl] = useState(item.documentUrl || '');
  const [showDocUrl, setShowDocUrl] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(item.label);

  function launchTarget() {
    if (item.linkType === 'route') return projectPath(item.linkTarget, project.id);
    return item.linkTarget;
  }

  function commitLabel() {
    setEditingLabel(false);
    const trimmed = labelDraft.trim();
    if (trimmed && trimmed !== item.label) onUpdate({ label: trimmed });
    else setLabelDraft(item.label);
  }

  function handleRemove() {
    if (window.confirm(`Remove "${item.label}" from this checklist?`)) onRemove();
  }

  return (
    <div style={{
      padding:'12px 14px', borderRadius:8,
      background: colors.bg, border: `1px solid ${colors.border}`,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <select
          className="form-select"
          style={{ width:130, fontSize:12, padding:'4px 8px' }}
          value={item.status}
          onChange={(e) => onUpdate({ status: e.target.value })}
        >
          {['Not Started', 'In Progress', 'Complete', 'N/A'].map((s) => <option key={s}>{s}</option>)}
        </select>

        <select
          className="form-select"
          title="Category"
          style={{ width:150, fontSize:12, padding:'4px 8px' }}
          value={item.category}
          onChange={(e) => onUpdate({ category: e.target.value })}
        >
          {CHECKLIST_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>

        <div style={{ flex:1 }}>
          {editingLabel ? (
            <input
              autoFocus
              type="text"
              className="form-input"
              style={{ fontSize:13, padding:'2px 6px' }}
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitLabel();
                if (e.key === 'Escape') { setLabelDraft(item.label); setEditingLabel(false); }
              }}
            />
          ) : (
            <div style={{
              fontSize:13, color: colors.text,
              textDecoration: item.status === 'Complete' ? 'line-through' : 'none',
            }}>
              {item.label}
            </div>
          )}
          {item.completedBy && (
            <div style={{ fontSize:11, color:'var(--text-mid)' }}>
              Completed by {item.completedBy.name}{item.completedAt ? ` · ${new Date(item.completedAt).toLocaleDateString()}` : ''}
            </div>
          )}
        </div>

        <input
          type="date"
          className="form-input"
          style={{ width:130, fontSize:12, padding:'4px 8px' }}
          value={item.dueDate ? item.dueDate.slice(0, 10) : ''}
          onChange={(e) => onUpdate({ dueDate: e.target.value || null })}
        />

        {item.linkType !== 'manual' && item.linkTarget && (
          item.linkType === 'route' ? (
            <Link to={launchTarget()} style={{ fontSize:12, color:'var(--primary-light)' }}>Open →</Link>
          ) : (
            <a href={launchTarget()} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--primary-light)' }}>Open →</a>
          )
        )}

        {item.documentUrl && (
          <a href={item.documentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize:12, color:'var(--primary-light)' }}>Doc →</a>
        )}

        <button
          onClick={() => setShowDocUrl((v) => !v)}
          title="Link to the document"
          style={{ fontSize:11, color: item.documentUrl ? 'var(--primary-light)' : 'var(--text-mid)', background:'none', border:'none', cursor:'pointer' }}
        >
          🔗
        </button>

        <button
          onClick={() => setShowNotes((v) => !v)}
          style={{ fontSize:11, color:'var(--text-mid)', background:'none', border:'none', cursor:'pointer' }}
        >
          📝
        </button>

        <button
          onClick={() => setEditingLabel(true)}
          title="Edit title"
          style={{ fontSize:11, color:'var(--primary-light)', background:'none', border:'none', cursor:'pointer' }}
        >
          ✏️
        </button>

        {onRemove && (
          <button onClick={handleRemove} title="Remove item" style={{ fontSize:11, color:'var(--red)', background:'none', border:'none', cursor:'pointer' }}>✕</button>
        )}
      </div>

      {showDocUrl && (
        <input
          type="url"
          className="form-input"
          style={{ marginTop:8, fontSize:12, width:'100%' }}
          placeholder="Link to where this document actually lives (SharePoint, etc.)…"
          value={docUrl}
          onChange={(e) => setDocUrl(e.target.value)}
          onBlur={() => { if (docUrl !== (item.documentUrl || '')) onUpdate({ documentUrl: docUrl || null }); }}
        />
      )}

      {showNotes && (
        <textarea
          className="form-input"
          style={{ marginTop:8, fontSize:12, width:'100%' }}
          rows={2}
          placeholder="Notes…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => { if (notes !== (item.notes || '')) onUpdate({ notes }); }}
        />
      )}
    </div>
  );
}

function AddItemForm({ onAdd }) {
  const [category, setCategory] = useState(CHECKLIST_CATEGORIES[0]);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleAdd() {
    if (!label.trim()) return;
    setSaving(true);
    try {
      await onAdd({ category, label: label.trim() });
      setLabel('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="card-title">Add a custom item</div>
      <div style={{ display:'flex', gap:10 }}>
        <select className="form-select" style={{ width:200 }} value={category} onChange={(e) => setCategory(e.target.value)}>
          {CHECKLIST_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input
          type="text" className="form-input" style={{ flex:1 }}
          placeholder="What needs to get done?"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
        />
        <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !label.trim()}>Add</button>
      </div>
    </div>
  );
}

// ── ASSIGN TEAM ─────────────────────────────────────────────
function AssignTeamPanel({ project, onSaved }) {
  const modules = project.sowDetails?.softwareModules
    ? project.sowDetails.softwareModules.split('\n').map((m) => m.trim()).filter(Boolean)
    : [];

  const [members, setMembers] = useState([]);
  const [projectManagerId, setProjectManagerId] = useState(project.projectManager?.id || '');
  const [conversionLeadId, setConversionLeadId] = useState(project.conversionLead?.id || '');
  const [moduleLeads, setModuleLeads] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [users, leads] = await Promise.all([
          apiFetch('/users'),
          apiFetch(`/projects/${project.id}/module-leads`),
        ]);
        if (cancelled) return;
        setMembers(users.filter((u) => u.isActive).slice().sort((a, b) => a.name.localeCompare(b.name)));
        setModuleLeads(Object.fromEntries(leads.map((l) => [l.moduleName, l.userId])));
      } catch (err) {
        if (!cancelled) setError('Could not load team members — ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project.id]);

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          projectManagerId: projectManagerId || null,
          conversionLeadId: conversionLeadId || null,
        }),
      });
      await apiFetch(`/projects/${project.id}/module-leads`, {
        method: 'PUT',
        body: JSON.stringify({
          moduleLeads: Object.entries(moduleLeads)
            .filter(([, userId]) => userId)
            .map(([moduleName, userId]) => ({ moduleName, userId })),
        }),
      });
      setSavedAt(new Date());
      await onSaved?.();
    } catch (err) {
      setError('Could not save team assignments — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <CollapsibleCard title="Assign project team">
      {error && (
        <div style={{ padding:'12px 16px', borderRadius:8, marginBottom:16, background:'rgba(239,65,54,0.08)',
          border:'1px solid rgba(239,65,54,0.3)', fontSize:13, color:'var(--red)' }}>
          ⚠ {error}
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Project Manager</label>
          <select
            className="form-select" value={projectManagerId} disabled={loading}
            onChange={(e) => setProjectManagerId(e.target.value)}
          >
            <option value="">— Unassigned —</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom:0 }}>
          <label className="form-label">Conversion / Migration Lead</label>
          <select
            className="form-select" value={conversionLeadId} disabled={loading}
            onChange={(e) => setConversionLeadId(e.target.value)}
          >
            <option value="">— Select —</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      {modules.length > 0 && (
        <>
          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--text-mid)', marginBottom:12 }}>
            Module Leads
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            {modules.map((module) => (
              <div key={module} className="form-group" style={{ marginBottom:0 }}>
                <label className="form-label">{module} Lead</label>
                <select
                  className="form-select" disabled={loading}
                  value={moduleLeads[module] || ''}
                  onChange={(e) => setModuleLeads((prev) => ({ ...prev, [module]: e.target.value }))}
                >
                  <option value="">— Select —</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save team assignments'}
        </button>
        {savedAt && <span style={{ fontSize:12, color:'var(--green)' }}>✓ Saved</span>}
      </div>
    </CollapsibleCard>
  );
}

// ── PM ACTIVITY (Judy's productivity/contribution view) ─────
function PmActivityPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setRows(await apiFetch('/project-launch/pm-activity'));
      } catch (err) {
        setError('Could not load PM activity — ' + err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ textAlign:'center', padding:32, color:'var(--text-mid)', fontSize:13 }}>Loading…</div>;
  if (error) {
    return (
      <div style={{ padding:'12px 16px', borderRadius:8, background:'rgba(239,65,54,0.08)',
        border:'1px solid rgba(239,65,54,0.3)', fontSize:13, color:'var(--red)' }}>
        ⚠ {error}
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-title">PM activity across their projects</div>
      <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:16 }}>
        Checklist items each PM has personally completed — auto-detected items (folders/site created)
        aren't counted here, since nobody did those by hand.
      </div>
      <table style={{ width:'100%', fontSize:13, borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ textAlign:'left', color:'var(--text-mid)', fontSize:11, textTransform:'uppercase' }}>
            <th style={{ padding:'6px 8px' }}>PM</th>
            <th style={{ padding:'6px 8px' }}>Projects</th>
            <th style={{ padding:'6px 8px' }}>Items Completed</th>
            <th style={{ padding:'6px 8px' }}>Overdue</th>
            <th style={{ padding:'6px 8px' }}>Last Activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.pmId} style={{ borderTop:'1px solid var(--border)' }}>
              <td style={{ padding:'8px' }}>{r.pmName}</td>
              <td style={{ padding:'8px' }}>{r.projectCount}</td>
              <td style={{ padding:'8px' }}>{r.itemsCompleted} of {r.itemsTotal}</td>
              <td style={{ padding:'8px', color: r.itemsOverdue ? 'var(--red)' : 'inherit' }}>{r.itemsOverdue}</td>
              <td style={{ padding:'8px' }}>{r.lastActiveAt ? new Date(r.lastActiveAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} style={{ padding:16, textAlign:'center', color:'var(--text-mid)' }}>No PM-assigned projects yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
